import {
  Box,
  Stack,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  TextField,
  IconButton,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Snackbar,
  Chip,
  Divider,
  Autocomplete,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  FormControlLabel,
  FormGroup,
  List,
  ListItemButton,
  ListItemText,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  AddRounded,
  DeleteOutlineRounded,
  ArrowBackRounded,
  SaveRounded,
  HistoryRounded,
  PlayArrowRounded,
  ChevronLeftRounded,
  ChevronRightRounded,
  ExpandMoreRounded,
  ExpandLessRounded,
  EditNoteRounded,
  LinkRounded,
} from '@mui/icons-material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { useTenantStore } from '../../../stores/tenant.store';
import { courseTemplateApi, type TextbookDto } from '../../../lib/api/course-template.api';
import { academicApi } from '../../../lib/api/academic.api';
import { PageEditor } from './PageEditor';
import { GenerateSyllabusWizard } from './GenerateSyllabusWizard';
import { SyllabusPdfPreviewButton } from './SyllabusPdfPreviewButton';
import { CopySectionDialog, type CopyableSectionKey } from './CopySectionDialog';
import { ModuleDayDeleteDialog } from './ModuleDayDeleteDialog';
import { SessionKeyTextsField } from './SessionKeyTextsField';
import { copyModules, deleteModuleDay, hasNoDay, sessionsOnDay } from './template-modules.model';
import { formatScriptureRefs } from '../../../lib/scripture-refs';
import { CoursePicker } from './CoursePicker';
import { ContentCopyRounded } from '@mui/icons-material';
import {
  asScheduleStyle,
  buildWorkloadRows,
  dayFitSummary,
  layoutDay,
  normalizeExpectedHours,
  resolveScheduleStyle,
  type DayFitSummary,
  type ScheduleStyle,
} from '@dosfilos/domain';
import { fitSentence, templateFitIssues } from '../../../lib/template-session-fit';
import { loadInheritance } from '../../../lib/composer/load-composed-syllabus';
import type { ComposedSyllabus } from '@dosfilos/application/composition';
import { errorText } from '../../../lib/error-text';
import { formatTime } from '../../../lib/i18n-format';
import { formatInstant } from '@dosfilos/shared';
import {
  useTenantDateFormat,
  useTenantTimeFormat,
  useTenantTimeZone,
} from '../../../stores/tenant.store';
import { useCanManageAcademics } from '../../../hooks/use-can-manage-academics';

export type TemplateState = {
  id: string;
  title: string;
  code?: string;
  programId?: string;
  /** FK to academic Course (Phase C — Course owns description/OACs/anchorVerse/biblicalVersion). */
  courseId?: string;
  version: number;
  description: Record<string, unknown>;
  institutionalFramework?: { missionText?: string; programPurpose?: Record<string, unknown> };
  objectives: {
    program: { id: string; text: string }[];
    department: { id: string; text: string }[];
    course: { id: string; text: string }[];
  };
  philosophy: { id: string; title: string; description: string }[];
  grading: {
    /** La evaluación es la unidad: el mismo ítem que lleva el peso lleva su
     *  descripción y los OAC a los que contribuye. Ver `GradingComponent`. */
    components: {
      id: string;
      name: string;
      weight: number;
      description?: Record<string, unknown>;
      objectiveIds?: string[];
      hours?: number;
    }[];
    passingGrade: number;
    expectedHours: { id: string; label: string; hours: number }[];
    note?: Record<string, unknown>;
  };
  readings: {
    required: { textbookId: string; notes?: string }[];
    complementary: {
      intro?: string;
      items: { textbookId: string; notes?: string }[];
    };
    plan?: {
      rows: {
        id: string;
        label: string;
        pages: number;
        topic?: string;
        textbookId?: string;
        /** Week of the term this reading belongs to (0 = first week). */
        weekOffset?: number;
      }[];
      introNote?: string;
      description?: string;
      closingNote?: string;
    };
  };
  sessionFormat: {
    weekdayTemplates: {
      id: string;
      weekday: Weekday;
      startTime: string;
      endTime: string;
      segments: {
        id: string;
        kind: 'section' | 'break';
        durationMinutes: number;
        label?: string;
      }[];
    }[];
    introNote?: string;
  };
  modules: ModuleTpl[];
  assignments: AssignTpl[];
  /** How the course calendar is presented. Absent = let the domain decide. */
  scheduleStyle?: ScheduleStyle;
  latePolicy?: {
    firstWindowHours: number;
    firstWindowPenalty: number;
    secondWindowHours: number;
    secondWindowPenalty: number;
    dailyPenaltyAfter: number;
  };
  policiesNote?: string;
  assignmentsNote?: Record<string, unknown>;
  anchorVerse?: { reference: string; text: string; translation: string };
  biblicalVersion?: string;
};

type Weekday = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

interface ModuleTpl {
  id: string;
  name: string;
  axisTopic: string;
  weekOffsets: number[];
  dayOffsets: { weekIndex: number; weekday: Weekday }[];
  sessions: SessionTpl[];
  /** Section codes this module applies to (domain ModuleTemplate). Not edited
   *  here, but kept: before S1·S2 the editor dropped it on save. */
  appliesToCodes?: string[];
}

interface SessionTpl {
  id: string;
  dayIndex: number;
  /** Optional duration override. When absent, generator uses block's duration. */
  durationMinutes?: number;
  topic: string;
  scriptureRefs?: { book: string; chapter: number; verses?: string }[];
}

interface AssignTpl {
  id: string;
  type: 'reading-review' | 'essay' | 'quiz' | 'project' | 'attendance' | 'other';
  name: string;
  description: string;
  /** Optional module this assignment belongs to. When set, generating the
   *  syllabus places the assignment as an item inside that module. */
  templateModuleId?: string;
  weekOffset: number;
  weight: number;
  gradeComponentId: string;
  pointsPossible: number;
  submissionTypes: ('file' | 'text' | 'url')[];
  /** FK to a rubric in Program.rubrics (fallback Tenant.config.rubrics). */
  rubricRef?: string;
}

/** Mirrors `requiresSubmissionChannel` / `defaultSubmissionTypes` in the domain.
 *  Kept local so the editor doesn't pull domain runtime into the web bundle. */
const requiresSubmissionChannel = (type: AssignTpl['type']): boolean =>
  type === 'reading-review' || type === 'essay' || type === 'project' || type === 'other';

const defaultSubmissionTypes = (type: AssignTpl['type']): AssignTpl['submissionTypes'] => {
  if (type === 'attendance') return [];
  if (type === 'quiz') return ['text'];
  return ['file', 'text'];
};

const WEEKDAYS: { value: Weekday; label: string }[] = [
  { value: 'monday', label: 'Lunes' },
  { value: 'tuesday', label: 'Martes' },
  { value: 'wednesday', label: 'Miércoles' },
  { value: 'thursday', label: 'Jueves' },
  { value: 'friday', label: 'Viernes' },
  { value: 'saturday', label: 'Sábado' },
  { value: 'sunday', label: 'Domingo' },
];

function uid(): string {
  return crypto.randomUUID();
}

/** Normalize legacy readings shape (Array complementary, pagesRequired on required). */
function normalizeReadings(raw: unknown): TemplateState['readings'] {
  const r = (raw ?? {}) as Record<string, unknown>;
  const rawRequired = (r['required'] as unknown[]) ?? [];
  const required = rawRequired
    .filter((it): it is Record<string, unknown> => typeof it === 'object' && it !== null)
    .map((it) => ({
      textbookId: it['textbookId'] as string,
      ...(it['notes'] ? { notes: it['notes'] as string } : {}),
    }));
  const rawComp = r['complementary'];
  let complementary: TemplateState['readings']['complementary'];
  if (Array.isArray(rawComp)) {
    complementary = {
      items: rawComp
        .filter((it): it is Record<string, unknown> => typeof it === 'object' && it !== null)
        .map((it) => ({
          textbookId: it['textbookId'] as string,
          ...(it['notes'] ? { notes: it['notes'] as string } : {}),
        })),
    };
  } else {
    const obj = (rawComp ?? {}) as Record<string, unknown>;
    complementary = {
      ...(obj['intro'] ? { intro: obj['intro'] as string } : {}),
      items: ((obj['items'] as unknown[]) ?? [])
        .filter((it): it is Record<string, unknown> => typeof it === 'object' && it !== null)
        .map((it) => ({
          textbookId: it['textbookId'] as string,
          ...(it['notes'] ? { notes: it['notes'] as string } : {}),
        })),
    };
  }
  if (r['plan']) {
    return {
      required,
      complementary,
      plan: r['plan'] as NonNullable<TemplateState['readings']['plan']>,
    };
  }
  return { required, complementary };
}

/** Normalize legacy modules — strip blockOffsetMinutes from sessions. */
function normalizeModules(raw: unknown): ModuleTpl[] {
  const list = (raw as Record<string, unknown>[]) ?? [];
  return list.map((m) => ({
    id: m['id'] as string,
    name: (m['name'] as string) ?? '',
    axisTopic: (m['axisTopic'] as string) ?? '',
    weekOffsets: (m['weekOffsets'] as number[]) ?? [],
    dayOffsets: (m['dayOffsets'] as { weekIndex: number; weekday: Weekday }[]) ?? [],
    ...(Array.isArray(m['appliesToCodes'])
      ? { appliesToCodes: m['appliesToCodes'] as string[] }
      : {}),
    sessions: ((m['sessions'] as Record<string, unknown>[]) ?? []).map((s) => ({
      id: s['id'] as string,
      dayIndex: (s['dayIndex'] as number) ?? 0,
      topic: (s['topic'] as string) ?? '',
      ...(typeof s['durationMinutes'] === 'number'
        ? { durationMinutes: s['durationMinutes'] as number }
        : {}),
      ...(s['scriptureRefs']
        ? {
            scriptureRefs: s['scriptureRefs'] as {
              book: string;
              chapter: number;
              verses?: string;
            }[],
          }
        : {}),
    })),
  }));
}

/** "20:00" + 90 → "21:30". Returns "" if startTime is malformed. */
function addMinutesToClock(startTime: string, minutes: number): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(startTime);
  if (!m) return '';
  const total = parseInt(m[1]!, 10) * 60 + parseInt(m[2]!, 10) + minutes;
  const h = Math.floor(total / 60) % 24;
  const mm = total % 60;
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

function clockToMinSafe(s: string): number {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s);
  if (!m) return 0;
  return parseInt(m[1]!, 10) * 60 + parseInt(m[2]!, 10);
}

/** Convert legacy { breaks } shape into the new segment sequence. */
function normalizeSessionFormat(raw: unknown): TemplateState['sessionFormat'] {
  const sf = (raw ?? {}) as Record<string, unknown>;
  const list = (sf['weekdayTemplates'] as Record<string, unknown>[]) ?? [];
  return {
    weekdayTemplates: list.map((w) => {
      const startTime = (w['startTime'] as string) ?? '00:00';
      const endTime = (w['endTime'] as string) ?? '00:00';
      const segs = w['segments'] as
        | { id: string; kind: 'section' | 'break'; durationMinutes: number; label?: string }[]
        | undefined;
      if (segs && segs.length > 0) {
        return {
          id: w['id'] as string,
          weekday: w['weekday'] as Weekday,
          startTime,
          endTime,
          segments: segs,
        };
      }
      // legacy: breaks[offset, duration]
      const breaks = ((w['breaks'] as Record<string, unknown>[]) ?? [])
        .map((b) => ({
          id: (b['id'] as string) ?? uid(),
          offsetMinutes: Number(b['offsetMinutes']) || 0,
          durationMinutes: Number(b['durationMinutes']) || 0,
          ...(b['label'] ? { label: b['label'] as string } : {}),
        }))
        .sort((a, b) => a.offsetMinutes - b.offsetMinutes);
      const total = clockToMinSafe(endTime) - clockToMinSafe(startTime);
      const segments: TemplateState['sessionFormat']['weekdayTemplates'][number]['segments'] = [];
      let cursor = 0;
      for (const b of breaks) {
        if (b.offsetMinutes > cursor) {
          segments.push({ id: uid(), kind: 'section', durationMinutes: b.offsetMinutes - cursor });
        }
        segments.push({
          id: b.id,
          kind: 'break',
          durationMinutes: b.durationMinutes,
          ...(b.label ? { label: b.label } : {}),
        });
        cursor = b.offsetMinutes + b.durationMinutes;
      }
      if (cursor < total) {
        segments.push({ id: uid(), kind: 'section', durationMinutes: total - cursor });
      }
      return {
        id: w['id'] as string,
        weekday: w['weekday'] as Weekday,
        startTime,
        endTime,
        segments,
      };
    }),
    ...(sf['introNote'] ? { introNote: sf['introNote'] as string } : {}),
  };
}

interface Props {
  templateId: string;
  onClose: () => void;
}

export function CourseTemplateEditor({ templateId, onClose }: Props): JSX.Element {
  const { t } = useTranslation('sis');
  // Tres acciones con texto más el título no entran en una fila angosta. Bajo
  // `md` las acciones se quedan con su ícono y el texto pasa al tooltip.
  const compactActions = useMediaQuery(useTheme().breakpoints.down('md'));
  const { currentTenantId, availableTenants } = useTenantStore();
  const [state, setRawState] = useState<TemplateState | null>(null);
  /**
   * Whether the editor holds changes that are not in Firestore yet.
   *
   * It exists for the PDF preview. The preview reloads the PERSISTED template and
   * runs the composer over it, then merges the in-memory state — and because the
   * composer falls back to the persisted template, the composed value wins for
   * description, objectives and the anchor verse. The result was a preview that
   * showed unsaved edits to readings and modules but the OLD text for those
   * fields, with nothing to indicate which was which.
   */
  const [dirty, setDirty] = useState(false);
  /** Every edit from a section goes through here, so nothing can change the
   *  template without the editor knowing it is now ahead of what was saved. */
  const setState: StateSetter = useCallback((next) => {
    setRawState(next);
    setDirty(true);
  }, []);
  const [textbooks, setTextbooks] = useState<TextbookDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const puedeGestionar = useCanManageAcademics();
  // S3: la copia de antes del último guardado que cambió algo, y si el último
  // «Guardar» no tenía cambios (no se crea versión).
  const [previous, setPrevious] = useState<{ version: number; savedAt: string } | null>(null);
  const [saveUnchanged, setSaveUnchanged] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  // S6: al guardar con sesiones que no caben enteras, se avisa (sin bloquear).
  const [fitConfirmOpen, setFitConfirmOpen] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoredMsg, setRestoredMsg] = useState<string | null>(null);
  const tenantTz = useTenantTimeZone();
  const tenantDateFmt = useTenantDateFormat();
  const tenantTimeFmt = useTenantTimeFormat();
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [tab, setTab] = useState(0);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [tenantBranding, setTenantBranding] = useState<{
    documentLogoUrl?: string;
    logoUrl?: string;
    mission?: { text: string; scripture?: string };
  }>({});
  const [programInfo, setProgramInfo] = useState<{ name?: string; purpose?: string }>({});
  // Read-only inherited content (Tenant → Department → Program → Course) shown in
  // the editor so the user sees what the syllabus composes from the linked entities.
  const [inherited, setInherited] = useState<ComposedSyllabus | null>(null);
  // Bump to force a re-fetch of `inherited` after promoting content to a source
  // entity (or to manually refresh when a source may have changed elsewhere).
  const [inheritedTick, setInheritedTick] = useState(0);
  const [programList, setProgramList] = useState<
    { id: string; name: string; code: string; courseIds: string[] }[]
  >([]);
  const [courseList, setCourseList] = useState<{ id: string; name: string; code: string }[]>([]);
  /** Rubrics catalog inherited from the linked Program (fallback Tenant). */
  const [availableRubrics, setAvailableRubrics] = useState<
    Array<{
      id: string;
      title: string;
      criteria: Array<{
        id: string;
        label: string;
        description: string;
        weight: number;
        levels?: Array<{ id: string; name: string; description: string }>;
      }>;
    }>
  >([]);
  // Copy-from-template state — drives the CopySectionDialog
  const [copyDialog, setCopyDialog] = useState<{
    section: CopyableSectionKey;
    label: string;
  } | null>(null);

  // S3: la versión bíblica que la plantilla tenía GUARDADA. Sin una propia, la
  // pantalla muestra «RV1960» (la del sistema) pero la plantilla hereda la del
  // curso: guardarla sin que nadie la eligiera pisaba esa herencia y, además,
  // hacía que «Guardar» sin cambios subiera la versión.
  const storedBiblicalVersion = useRef<string | undefined>(undefined);

  const reload = useCallback(async () => {
    if (!currentTenantId) return;
    setLoading(true);
    setError(null);
    try {
      const snap = await getDoc(
        doc(db, `tenants/${currentTenantId}/courseTemplates/${templateId}`),
      );
      if (!snap.exists()) throw new Error('Plantilla no encontrada');
      const data = snap.data();
      storedBiblicalVersion.current = data['biblicalVersion'] ?? undefined;
      setRawState({
        id: templateId,
        title: data['title'],
        code: data['code'] ?? undefined,
        programId: data['programId'] ?? undefined,
        courseId: data['courseId'] ?? undefined,
        version: data['version'] ?? 1,
        description: data['description'] ?? { type: 'doc', content: [{ type: 'paragraph' }] },
        institutionalFramework: data['institutionalFramework'] ?? undefined,
        objectives: data['objectives'] ?? { program: [], department: [], course: [] },
        philosophy: data['philosophy'] ?? [],
        grading: data['grading']
          ? {
              ...data['grading'],
              // Legacy fixed object → dynamic list (or pass an existing list through).
              expectedHours: normalizeExpectedHours(data['grading'].expectedHours),
            }
          : {
              components: [],
              passingGrade: 4,
              expectedHours: [],
            },
        readings: normalizeReadings(data['readings']),
        sessionFormat: normalizeSessionFormat(data['sessionFormat']),
        modules: normalizeModules(data['modules']),
        assignments: data['assignments'] ?? [],
        ...(asScheduleStyle(data['scheduleStyle'])
          ? { scheduleStyle: asScheduleStyle(data['scheduleStyle'])! }
          : {}),
        latePolicy: data['latePolicy'] ?? undefined,
        ...(data['policiesNote'] ? { policiesNote: data['policiesNote'] as string } : {}),
        ...(data['assignmentsNote']
          ? { assignmentsNote: data['assignmentsNote'] as Record<string, unknown> }
          : {}),
        anchorVerse: data['anchorVerse'] ?? undefined,
        biblicalVersion: data['biblicalVersion'] ?? 'RV1960',
      });
      const tbList = await import('firebase/firestore').then(async (m) => {
        const tbSnap = await m.getDocs(m.collection(db, `tenants/${currentTenantId}/textbooks`));
        return tbSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }) as TextbookDto & { deletedAt?: unknown })
          .filter((t) => !t.deletedAt);
      });
      setTextbooks(tbList);

      // Load programs list (for the Identity tab dropdown to set programId).
      // Filter to status === 'active' only — inactive + archived programs are
      // soft-deleted at the academic structure and shouldn't be assignable.
      const programsSnap = await import('firebase/firestore').then(async (m) => {
        const s = await m.getDocs(m.collection(db, `tenants/${currentTenantId}/programs`));
        return s.docs
          .filter((d) => ((d.data()['status'] as string | undefined) ?? 'active') === 'active')
          .map((d) => {
            const pd = d.data();
            // The curriculum travels with the program: the Identity tab offers
            // only the programs whose plan declares the linked course.
            const planned = new Set<string>();
            for (const version of (pd['curriculumVersions'] as
              | { terms?: { recommendedCourseIds?: string[] }[] }[]
              | undefined) ?? []) {
              for (const term of version.terms ?? []) {
                for (const id of term.recommendedCourseIds ?? []) planned.add(id);
              }
            }
            return {
              id: d.id,
              name: (pd['name'] as string) ?? d.id,
              code: (pd['code'] as string) ?? '',
              courseIds: [...planned],
            };
          });
      });
      setProgramList(programsSnap);

      // Load courses list (for the Identity tab dropdown to set courseId —
      // links the template to its catalog course; required so the section
      // freezes the transversal snapshot at creation).
      const coursesSnap = await import('firebase/firestore').then(async (m) => {
        const s = await m.getDocs(m.collection(db, `tenants/${currentTenantId}/courses`));
        return s.docs
          .filter((d) => !(d.data()['deletedAt'] as unknown))
          .map((d) => {
            const cd = d.data();
            return {
              id: d.id,
              name: (cd['name'] as string) ?? d.id,
              code: (cd['code'] as string) ?? '',
            };
          });
      });
      setCourseList(coursesSnap);

      // Tenant branding (logo + mission) for PDF
      const tenantSnap = await getDoc(doc(db, `tenants/${currentTenantId}`));
      if (tenantSnap.exists()) {
        const cfg = (tenantSnap.data()['config'] ?? {}) as Record<string, unknown>;
        setTenantBranding({
          ...(cfg['logoUrl'] ? { logoUrl: cfg['logoUrl'] as string } : {}),
          ...(cfg['documentLogoUrl'] ? { documentLogoUrl: cfg['documentLogoUrl'] as string } : {}),
          ...(cfg['mission']
            ? { mission: cfg['mission'] as { text: string; scripture?: string } }
            : {}),
        });
      }

      // The program is the one the TEMPLATE declares. It used to be resolved
      // through the course (`course.programId`), so this panel showed a program
      // the template had not chosen and the section might not use either — the
      // same guess the PDF preview was making, and the reason the two could
      // disagree with the student's document.
      const tplProgramId = data['programId'] as string | undefined;
      if (tplProgramId) {
        const progSnap = await getDoc(
          doc(db, `tenants/${currentTenantId}/programs/${tplProgramId}`),
        );
        if (progSnap.exists()) {
          const p = progSnap.data();
          setProgramInfo({
            ...(p['name'] ? { name: p['name'] as string } : {}),
            ...(p['purpose'] ? { purpose: p['purpose'] as string } : {}),
          });
        }
      }
    } catch (e) {
      setError(errorText(e, 'Error al cargar'));
    } finally {
      setLoading(false);
    }
  }, [currentTenantId, templateId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // S3: ¿hay versión anterior para restaurar? Se vuelve a preguntar con cada
  // versión nueva (guardar o restaurar la cambian).
  const loadedVersion = state?.version;
  useEffect(() => {
    if (!currentTenantId || !puedeGestionar || loadedVersion === undefined) return;
    let cancelled = false;
    courseTemplateApi
      .getPrevious({ tenantId: currentTenantId, templateId })
      .then((r) => {
        if (!cancelled) setPrevious(r.previous);
      })
      .catch(() => {
        if (!cancelled) setPrevious(null);
      });
    return () => {
      cancelled = true;
    };
  }, [currentTenantId, templateId, puedeGestionar, loadedVersion]);

  const restorePrevious = async () => {
    if (!currentTenantId) return;
    setRestoring(true);
    setError(null);
    try {
      const r = await courseTemplateApi.restorePrevious({ tenantId: currentTenantId, templateId });
      setRestoreOpen(false);
      setDirty(false);
      await reload();
      setRestoredMsg(
        t('templateEditor.history.restored', { from: r.restoredFrom, version: r.version }),
      );
    } catch (e) {
      setError(errorText(e));
    } finally {
      setRestoring(false);
    }
  };

  // Load the read-only inherited content whenever the linked course/program changes.
  useEffect(() => {
    if (!currentTenantId) return;
    const courseId = state?.courseId;
    const programId = state?.programId;
    if (!courseId && !programId) {
      setInherited(null);
      return;
    }
    let cancelled = false;
    loadInheritance(currentTenantId, courseId, programId)
      .then((c) => {
        if (!cancelled) setInherited(c);
      })
      .catch(() => {
        if (!cancelled) setInherited(null);
      });
    return () => {
      cancelled = true;
    };
  }, [currentTenantId, state?.courseId, state?.programId, inheritedTick]);

  // Phase C.1: load rubric catalog inherited from the template's program
  // (fallback Tenant) so the Assignments tab can offer rubricRef dropdown.
  useEffect(() => {
    if (!currentTenantId) return;
    void (async () => {
      type FullRubric = (typeof availableRubrics)[number];
      let list: FullRubric[] = [];
      if (state?.programId) {
        const progSnap = await getDoc(
          doc(db, `tenants/${currentTenantId}/programs/${state.programId}`),
        );
        if (progSnap.exists()) {
          const p = progSnap.data();
          const progRubrics = (p['rubrics'] as FullRubric[] | undefined) ?? [];
          const legacy = p['researchRubric'] as FullRubric | undefined;
          list = progRubrics.length > 0 ? progRubrics : legacy ? [legacy] : [];
        }
      }
      if (list.length === 0) {
        const tenSnap = await getDoc(doc(db, `tenants/${currentTenantId}`));
        if (tenSnap.exists()) {
          const cfg = (tenSnap.data()['config'] ?? {}) as Record<string, unknown>;
          const tenRubrics = (cfg['rubrics'] as FullRubric[] | undefined) ?? [];
          const legacy = cfg['researchRubric'] as FullRubric | undefined;
          list = tenRubrics.length > 0 ? tenRubrics : legacy ? [legacy] : [];
        }
      }
      setAvailableRubrics(list);
    })();
  }, [currentTenantId, state?.programId]);

  /** Returns whether the save actually landed — the PDF preview needs to know,
   *  or it would open a document of the STALE stored template after a failure. */
  const save = async (): Promise<boolean> => {
    if (!currentTenantId || !state) return false;
    setSaving(true);
    setError(null);
    try {
      const { version, unchanged } = await courseTemplateApi.updateTemplate({
        tenantId: currentTenantId,
        templateId,
        patch: {
          title: state.title,
          ...(state.code ? { code: state.code } : {}),
          ...(state.programId ? { programId: state.programId } : {}),
          ...(state.courseId ? { courseId: state.courseId } : {}),
          description: state.description,
          institutionalFramework: state.institutionalFramework,
          objectives: state.objectives,
          philosophy: state.philosophy,
          grading: state.grading,
          readings: state.readings,
          sessionFormat: state.sessionFormat,
          modules: state.modules,
          assignments: state.assignments,
          // `null`, not an omitted key: the patch is MERGED over the stored props,
          // so leaving it out re-persists the old explicit choice and "Automática"
          // would never take effect.
          scheduleStyle: state.scheduleStyle ?? null,
          ...(state.latePolicy ? { latePolicy: state.latePolicy } : {}),
          ...(state.policiesNote ? { policiesNote: state.policiesNote } : {}),
          ...(state.assignmentsNote ? { assignmentsNote: state.assignmentsNote } : {}),
          ...(state.anchorVerse ? { anchorVerse: state.anchorVerse } : {}),
          ...(state.biblicalVersion &&
          (storedBiblicalVersion.current !== undefined || state.biblicalVersion !== 'RV1960')
            ? { biblicalVersion: state.biblicalVersion }
            : {}),
        },
      });
      setRawState({ ...state, version });
      setDirty(false);
      setSaveUnchanged(!!unchanged);
      if (storedBiblicalVersion.current !== undefined || state.biblicalVersion !== 'RV1960') {
        storedBiblicalVersion.current = state.biblicalVersion;
      }
      setSavedAt(new Date());
      return true;
    } catch (e) {
      setError(errorText(e, 'Error al guardar'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  if (loading || !state) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const promoteCtx: PromoteCtx | undefined = currentTenantId
    ? {
        tenantId: currentTenantId,
        courseId: state.courseId,
        programId: state.programId,
        departmentId: inherited?.departmentId,
        onDone: () => setInheritedTick((x) => x + 1),
      }
    : undefined;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <IconButton onClick={onClose}>
          <ArrowBackRounded />
        </IconButton>
        {/* `minWidth: 0` es lo que permite que el título se recorte en vez de
            empujar los botones fuera de la pantalla: sin eso un ítem flex no
            baja de su ancho de contenido. */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography variant="h5" fontWeight={700} noWrap sx={{ minWidth: 0 }}>
              {state.title}
            </Typography>
            {state.code && <Chip label={state.code} size="small" />}
            <Chip label={`v${state.version}`} size="small" variant="outlined" />
          </Stack>
          {savedAt && (
            <Typography variant="caption" color="success.main">
              {t('templateEditor.history.savedAt', { time: formatTime(savedAt) })}
            </Typography>
          )}
        </Box>
        <SyllabusPdfPreviewButton
          compact={compactActions}
          template={state}
          templateId={state.id}
          hasUnsavedChanges={dirty}
          onSaveFirst={save}
          textbooks={textbooks}
          tenantName={availableTenants.find((t) => t.id === currentTenantId)?.name ?? 'Seminario'}
          {...((tenantBranding.documentLogoUrl ?? tenantBranding.logoUrl)
            ? {
                documentLogoUrl: (tenantBranding.documentLogoUrl ??
                  tenantBranding.logoUrl) as string,
              }
            : {})}
          {...(tenantBranding.mission ? { mission: tenantBranding.mission } : {})}
          {...(programInfo.name ? { programName: programInfo.name } : {})}
          {...(programInfo.purpose ? { programPurpose: programInfo.purpose } : {})}
          referencedRubrics={(() => {
            const refIds = new Set<string>();
            for (const a of state.assignments) {
              if (a.rubricRef) refIds.add(a.rubricRef);
            }
            return availableRubrics.filter((r) => refIds.has(r.id));
          })()}
        />
        {compactActions ? (
          <Tooltip title={t('templateEditor.generate.button')}>
            <span>
              <IconButton
                color="primary"
                aria-label={t('templateEditor.generate.button')}
                onClick={() => setGenerateOpen(true)}
              >
                <PlayArrowRounded />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Button
            variant="outlined"
            startIcon={<PlayArrowRounded />}
            onClick={() => setGenerateOpen(true)}
          >
            {t('templateEditor.generate.button')}
          </Button>
        )}
        {/* Guardar CONSERVA su texto también en angosto: es la acción que el
            usuario busca cuando ya escribió algo, y un ícono solo en la esquina
            de una pantalla chica es justo lo que no se encuentra a tiempo. */}
        {/* SÓLO GESTIÓN GUARDA. La plantilla la comparten TODAS las secciones
            del curso, así que editarla cambia el syllabus de los cursos de los
            colegas; el docente propone desde su propia sección, que es para lo
            que existe ese camino. El servidor lo exige igual — esto evita
            ofrecer un botón que respondería «no tienes permiso», que se lee
            como una falla del sistema y no como una decisión. */}
        {puedeGestionar &&
          previous &&
          (compactActions ? (
            <Tooltip title={t('templateEditor.history.restore')}>
              <IconButton
                aria-label={t('templateEditor.history.restore')}
                onClick={() => setRestoreOpen(true)}
                disabled={saving || restoring}
              >
                <HistoryRounded />
              </IconButton>
            </Tooltip>
          ) : (
            <Button
              variant="outlined"
              startIcon={<HistoryRounded />}
              onClick={() => setRestoreOpen(true)}
              disabled={saving || restoring}
              sx={{ flexShrink: 0 }}
            >
              {t('templateEditor.history.restore')}
            </Button>
          ))}
        {puedeGestionar ? (
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <SaveRounded />}
            onClick={() =>
              state &&
              templateFitIssues(state.modules, state.sessionFormat.weekdayTemplates).length > 0
                ? setFitConfirmOpen(true)
                : void save()
            }
            disabled={saving}
            sx={{ flexShrink: 0 }}
          >
            {t('templateEditor.actions.save')}
          </Button>
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
            {t('templateEditor.readOnly')}
          </Typography>
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="Identidad" />
          <Tab label="Descripción" />
          <Tab label="Objetivos" />
          <Tab label="Filosofía" />
          <Tab label="Evaluación" />
          <Tab label="Lecturas" />
          <Tab label="Formato jornadas" />
          <Tab label="Módulos" />
          <Tab label="Tareas" />
          <Tab label="Políticas" />
        </Tabs>
      </Paper>

      {tab === 0 && (
        <IdentitySection
          state={state}
          setState={setState}
          inherited={inherited}
          programs={programList}
          courses={courseList}
        />
      )}
      {tab === 1 && (
        <>
          <InheritedFromCourseBanner courseId={state.courseId} fieldLabel="Descripción del Curso" />
          <SectionCopyBar
            onOpen={() => setCopyDialog({ section: 'description', label: 'Descripción' })}
          />
          <DescriptionSection
            state={state}
            setState={setState}
            inherited={inherited}
            promote={promoteCtx}
          />
        </>
      )}
      {tab === 2 && (
        <>
          <InheritedFromCourseBanner
            courseId={state.courseId}
            fieldLabel="Objetivos del Curso (OAC)"
          />
          <SectionCopyBar
            onOpen={() =>
              setCopyDialog({ section: 'objectives.course', label: 'Objetivos del curso' })
            }
          />
          <ObjectivesSection
            state={state}
            setState={setState}
            inherited={inherited}
            promote={promoteCtx}
          />
        </>
      )}
      {tab === 3 && (
        <>
          <SectionCopyBar
            onOpen={() => setCopyDialog({ section: 'philosophy', label: 'Filosofía' })}
          />
          <PhilosophySection
            state={state}
            setState={setState}
            inherited={inherited}
            promote={promoteCtx}
          />
        </>
      )}
      {tab === 4 && (
        <>
          <SectionCopyBar
            onOpen={() => setCopyDialog({ section: 'grading', label: 'Evaluación' })}
          />
          <GradingSection state={state} setState={setState} inherited={inherited} />
        </>
      )}
      {tab === 5 && (
        <>
          <SectionCopyBar
            onOpen={() => setCopyDialog({ section: 'readings', label: 'Lecturas' })}
          />
          <ReadingsSection state={state} setState={setState} textbooks={textbooks} />
        </>
      )}
      {tab === 6 && (
        <>
          <SectionCopyBar
            onOpen={() => setCopyDialog({ section: 'sessionFormat', label: 'Formato de jornadas' })}
          />
          <SessionFormatSection state={state} setState={setState} />
        </>
      )}
      {tab === 7 && (
        <>
          <SectionCopyBar onOpen={() => setCopyDialog({ section: 'modules', label: 'Módulos' })} />
          <ModulesSection state={state} setState={setState} />
        </>
      )}
      {tab === 8 && (
        <>
          <SectionCopyBar
            onOpen={() => setCopyDialog({ section: 'assignments', label: 'Tareas' })}
          />
          <AssignmentsSection
            state={state}
            setState={setState}
            availableRubrics={availableRubrics}
          />
        </>
      )}
      {tab === 9 && (
        <>
          <SectionCopyBar
            onOpen={() => setCopyDialog({ section: 'latePolicy', label: 'Política de tardanza' })}
          />
          <PoliciesSection state={state} setState={setState} inherited={inherited} />
        </>
      )}

      {copyDialog && currentTenantId && (
        <CopySectionDialog
          open={!!copyDialog}
          onClose={() => setCopyDialog(null)}
          section={copyDialog.section}
          sectionLabel={copyDialog.label}
          tenantId={currentTenantId}
          currentTemplateId={templateId}
          onCopy={(value, options) => {
            if (!state) return;
            switch (copyDialog.section) {
              case 'description':
                setState({ ...state, description: value as Record<string, unknown> });
                break;
              case 'objectives.course':
                setState({
                  ...state,
                  objectives: {
                    ...state.objectives,
                    course: value as { id: string; text: string }[],
                  },
                });
                break;
              case 'philosophy':
                setState({ ...state, philosophy: value as typeof state.philosophy });
                break;
              case 'grading':
                setState({ ...state, grading: value as typeof state.grading });
                break;
              case 'readings':
                setState({ ...state, readings: value as typeof state.readings });
                break;
              case 'sessionFormat':
                setState({ ...state, sessionFormat: value as typeof state.sessionFormat });
                break;
              case 'modules':
                // S2: «Solo la estructura» no trae temas ni textos clave de otra materia.
                setState({
                  ...state,
                  modules: copyModules(normalizeModules(value), options.modulesMode, (n) =>
                    t('templateEditor.modules.sessions.genericTopic', { n }),
                  ),
                });
                break;
              case 'assignments':
                setState({ ...state, assignments: value as typeof state.assignments });
                break;
              case 'latePolicy':
                setState({
                  ...state,
                  latePolicy: value as NonNullable<typeof state.latePolicy>,
                });
                break;
              case 'anchorVerse':
                setState({
                  ...state,
                  anchorVerse: value as NonNullable<typeof state.anchorVerse>,
                });
                break;
            }
          }}
        />
      )}

      <Snackbar
        open={!!savedAt}
        autoHideDuration={saveUnchanged ? 4000 : 2200}
        onClose={() => setSavedAt(null)}
        message={
          saveUnchanged ? t('templateEditor.history.unchanged') : t('templateEditor.history.saved')
        }
      />
      <Snackbar
        open={!!restoredMsg}
        autoHideDuration={4000}
        onClose={() => setRestoredMsg(null)}
        message={restoredMsg ?? ''}
      />

      {/* S3: restaurar la versión anterior — con confirmación. */}
      <Dialog
        open={restoreOpen && !!previous}
        onClose={restoring ? undefined : () => setRestoreOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('templateEditor.history.restoreTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Typography variant="body2">
              {previous &&
                t('templateEditor.history.restoreBody', {
                  version: previous.version,
                  when: formatInstant(previous.savedAt, tenantTz, tenantDateFmt, tenantTimeFmt),
                })}
            </Typography>
            {dirty && (
              <Alert severity="warning">{t('templateEditor.history.restoreUnsaved')}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreOpen(false)} disabled={restoring}>
            {t('templateEditor.history.restoreCancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => void restorePrevious()}
            disabled={restoring}
            startIcon={restoring ? <CircularProgress size={16} /> : <HistoryRounded />}
          >
            {t('templateEditor.history.restoreConfirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* S6: guardar con sesiones recortadas o que no caben — se avisa, no se bloquea. */}
      <Dialog
        open={fitConfirmOpen}
        onClose={() => setFitConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('templateEditor.modules.fit.saveTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Typography variant="body2">{t('templateEditor.modules.fit.saveBody')}</Typography>
            {fitConfirmOpen &&
              state &&
              templateFitIssues(state.modules, state.sessionFormat.weekdayTemplates).map((x) => (
                <Alert key={`${x.moduleIndex}-${x.dayIndex}`} severity="warning">
                  <Typography variant="body2" fontWeight={700}>
                    {t('templateEditor.modules.fit.saveItem', {
                      n: x.moduleIndex + 1,
                      name: x.moduleName,
                      day: t('templateEditor.modules.days.label', {
                        n: x.dayIndex + 1,
                        weekday: t(`templateEditor.modules.weekdays.${x.weekday}`),
                        week: x.weekIndex + 1,
                      }),
                    })}
                  </Typography>
                  <Typography variant="body2">{fitSentence(t, x.summary, x.weekday)}</Typography>
                </Alert>
              ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFitConfirmOpen(false)}>
            {t('templateEditor.modules.fit.saveBack')}
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveRounded />}
            onClick={() => {
              setFitConfirmOpen(false);
              void save();
            }}
          >
            {t('templateEditor.modules.fit.saveAnyway')}
          </Button>
        </DialogActions>
      </Dialog>

      {generateOpen && (
        <GenerateSyllabusWizard
          // `state` ya trae `courseId`: es lo que el asistente usa para no
          // ofrecer por defecto las secciones de otras materias.
          template={state}
          onClose={() => setGenerateOpen(false)}
          onGenerated={() => {
            setGenerateOpen(false);
          }}
        />
      )}
    </Box>
  );
}

type StateSetter = React.Dispatch<React.SetStateAction<TemplateState | null>>;

/**
 * Ids + refresh callback needed to promote an editable template field to its
 * source entity (Course / Program / Department) from within a section.
 */
interface PromoteCtx {
  tenantId: string;
  courseId?: string | undefined;
  programId?: string | undefined;
  departmentId?: string | undefined;
  /** Called after a successful promote to refetch the inherited preview. */
  onDone: () => void;
}

interface SectionProps {
  state: TemplateState;
  setState: StateSetter;
  /** Read-only inherited content from the linked Course/Program/Department/Tenant. */
  inherited?: ComposedSyllabus | null;
  /** Present when the template is linked; enables "save to source" actions. */
  promote?: PromoteCtx | undefined;
}

/** Objective items → the {id, text} DTO the source callables expect (drops blanks). */
function toObjectiveDtos(items: { id: string; text: string }[]): { id: string; text: string }[] {
  return items.filter((o) => o.text.trim()).map((o) => ({ id: o.id, text: o.text.trim() }));
}

/** Extracts plain text from a TipTap JSON doc (for read-only inherited previews). */
function tiptapToPlainText(json: unknown): string {
  const walk = (node: any): string => {
    if (!node) return '';
    if (typeof node.text === 'string') return node.text;
    const kids = Array.isArray(node.content) ? node.content : [];
    const sep = node.type === 'paragraph' || node.type === 'heading' ? '\n' : '';
    return kids.map(walk).join('') + sep;
  };
  return walk(json).trim();
}

/**
 * A dashed read-only card that surfaces content inherited from a linked entity
 * (Course/Program/Department/Tenant). Shows an "empty" hint when the source has
 * no data, so the user knows to fill it in that entity.
 */
function InheritedBlock({
  label,
  hasContent,
  emptyHint,
  children,
}: {
  label: string;
  hasContent: boolean;
  emptyHint: string;
  children?: React.ReactNode;
}): JSX.Element {
  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, borderRadius: 2, borderStyle: 'dashed', bgcolor: 'action.hover' }}
    >
      <Stack direction="row" alignItems="center" spacing={0.5} mb={hasContent ? 1 : 0.5}>
        <LinkRounded sx={{ fontSize: 15, color: 'text.secondary' }} />
        <Typography variant="caption" fontWeight={700} color="text.secondary">
          {label}
        </Typography>
      </Stack>
      {hasContent ? (
        children
      ) : (
        <Typography variant="caption" color="text.disabled">
          {emptyHint}
        </Typography>
      )}
    </Paper>
  );
}

/** Read-only bulleted list of inherited objective items. */
function InheritedObjectives({ items }: { items: { code?: string; text: string }[] }): JSX.Element {
  return (
    <Stack component="ul" sx={{ m: 0, pl: 2.5 }} spacing={0.25}>
      {items.map((o, i) => (
        <Typography key={i} component="li" variant="body2" color="text.secondary">
          {o.code ? <strong>{o.code}. </strong> : ''}
          {o.text}
        </Typography>
      ))}
    </Stack>
  );
}

/**
 * Button + confirm dialog that promotes the current editable template field into
 * its source entity (Course/Program/Department) via the academic callables, then
 * refreshes the inherited preview so the field flips to read-only. Shared entities
 * (Program/Department) show a scope warning.
 */
function PromoteToSource({
  entityLabel,
  shared,
  disabled,
  onConfirm,
}: {
  entityLabel: string;
  shared: boolean;
  disabled: boolean;
  onConfirm: () => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const run = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      await onConfirm();
      setOpen(false);
    } catch (e) {
      setErr(errorText(e, t('templateEditor.promote.error')));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<SaveRounded />}
        disabled={disabled}
        onClick={() => setOpen(true)}
        sx={{ textTransform: 'none' }}
      >
        {t('templateEditor.promote.button', { entity: entityLabel })}
      </Button>
      <Dialog open={open} onClose={() => (busy ? undefined : setOpen(false))}>
        <DialogTitle>{t('templateEditor.promote.title', { entity: entityLabel })}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('templateEditor.promote.body', { entity: entityLabel })}
          </DialogContentText>
          {shared && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('templateEditor.promote.sharedWarning', { entity: entityLabel })}
            </Alert>
          )}
          {err && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {err}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={busy}>
            {t('templateEditor.promote.cancel')}
          </Button>
          <Button
            onClick={run}
            variant="contained"
            disabled={busy}
            startIcon={busy ? <CircularProgress size={16} /> : undefined}
          >
            {t('templateEditor.promote.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function SectionCopyBar({ onOpen }: { onOpen: () => void }): JSX.Element {
  return (
    <Stack direction="row" justifyContent="flex-end" mb={1}>
      <Button
        size="small"
        variant="text"
        startIcon={<ContentCopyRounded fontSize="small" />}
        onClick={onOpen}
        sx={{ textTransform: 'none' }}
      >
        Copiar desde otra plantilla
      </Button>
    </Stack>
  );
}

/**
 * Banner shown on tabs whose data lives at the Course level (description,
 * objectives.course, anchorVerse, biblicalVersion). Phase C — Course is the
 * academic identity source of truth; the template only stores the legacy
 * fallback. When the template is linked to a Course, send the user to the
 * Course detail page to edit; otherwise prompt them to link first.
 */
function InheritedFromCourseBanner({
  courseId,
  fieldLabel,
}: {
  courseId: string | undefined;
  fieldLabel: string;
}): JSX.Element {
  return (
    <Alert
      severity="info"
      variant="outlined"
      sx={{ mb: 2, borderRadius: 2 }}
      action={
        courseId ? (
          <Button
            component={RouterLink}
            to={`/academic/courses/${courseId}`}
            size="small"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Editar en Curso →
          </Button>
        ) : null
      }
    >
      <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
        {fieldLabel}: heredado del Curso
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {courseId
          ? 'Esta sección es de solo lectura — la fuente de verdad es el Curso vinculado.'
          : 'Esta plantilla aún no está vinculada a un Curso. Los valores aquí se usan como respaldo hasta migrar.'}
      </Typography>
    </Alert>
  );
}

/**
 * Which calendar the syllabus prints. The two are different documents, not
 * styles: a weekly table for a course taught asynchronously, a date-and-clock
 * table for one with class days. The default is SHOWN rather than merely
 * applied, so the author can see what the document will do before choosing.
 */
function ScheduleStylePicker({ state, setState }: SectionProps): JSX.Element {
  const { t } = useTranslation('sis');
  const effective = resolveScheduleStyle(state);
  return (
    <TextField
      select
      size="small"
      label={t('templateEditor.identity.scheduleStyle')}
      value={state.scheduleStyle ?? ''}
      onChange={(e) => {
        const picked = e.target.value as ScheduleStyle | '';
        if (picked === '') {
          const { scheduleStyle: _cleared, ...rest } = state;
          setState(rest);
        } else {
          setState({ ...state, scheduleStyle: picked });
        }
      }}
      helperText={t('templateEditor.identity.scheduleStyleHelp', {
        effective: t(`templateEditor.identity.scheduleStyleOption.${effective}`),
      })}
    >
      <MenuItem value="">{t('templateEditor.identity.scheduleStyleAuto')}</MenuItem>
      <MenuItem value="weekly">{t('templateEditor.identity.scheduleStyleOption.weekly')}</MenuItem>
      <MenuItem value="sessions">
        {t('templateEditor.identity.scheduleStyleOption.sessions')}
      </MenuItem>
    </TextField>
  );
}

function IdentitySection({
  state,
  setState,
  inherited,
  programs,
  courses,
}: SectionProps & {
  programs: { id: string; name: string; code: string; courseIds: string[] }[];
  courses: { id: string; name: string; code: string }[];
}): JSX.Element {
  const { t } = useTranslation('sis');
  // Which programs may claim this template: the ones whose curriculum declares
  // its course. With no course linked there is nothing to check against, so the
  // whole list stays available. A program the template is ALREADY pointing at is
  // never hidden — dropping the current value silently would look like the
  // selector lost it.
  const programOptions = state.courseId
    ? programs.filter((p) => p.courseIds.includes(state.courseId!) || p.id === state.programId)
    : programs;
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack spacing={2}>
        <TextField
          label={t('templateEditor.identity.title')}
          size="small"
          value={state.title}
          onChange={(e) => setState({ ...state, title: e.target.value })}
        />
        <TextField
          label={t('templateEditor.identity.code')}
          size="small"
          value={state.code ?? ''}
          onChange={(e) => setState({ ...state, code: e.target.value })}
        />
        <ScheduleStylePicker state={state} setState={setState} />
        <Box>
          <CoursePicker
            value={state.courseId ?? ''}
            onChange={(cid) => {
              if (cid) {
                setState({ ...state, courseId: cid });
              } else {
                const { courseId: _drop, ...rest } = state;
                void _drop;
                setState(rest);
              }
            }}
            courses={courses}
          />
          <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
            {t('templateEditor.identity.courseHelp')}
          </Typography>
        </Box>
        <FormControl size="small">
          <InputLabel id="program-select-label">{t('templateEditor.identity.program')}</InputLabel>
          <Select
            labelId="program-select-label"
            label={t('templateEditor.identity.program')}
            value={state.programId ?? ''}
            onChange={(e) => {
              const v = e.target.value as string;
              if (v) {
                setState({ ...state, programId: v });
              } else {
                const { programId: _drop, ...rest } = state;
                void _drop;
                setState(rest);
              }
            }}
          >
            <MenuItem value="">
              <em>{t('templateEditor.identity.programNone')}</em>
            </MenuItem>
            {programOptions.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.code ? `${p.code} — ` : ''}
                {p.name}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="caption" color="text.secondary" mt={0.5}>
            {state.courseId && programOptions.length === 0
              ? t('templateEditor.identity.programNoneIncludeCourse')
              : t('templateEditor.identity.programHelp')}
          </Typography>
        </FormControl>
        <TextField
          label={t('templateEditor.identity.biblicalVersion')}
          size="small"
          value={state.biblicalVersion ?? ''}
          disabled={Boolean(state.courseId)}
          helperText={state.courseId ? t('templateEditor.identity.inheritedFromCourse') : undefined}
          onChange={(e) => setState({ ...state, biblicalVersion: e.target.value })}
          placeholder="RV1960"
        />

        {inherited && (inherited.programName || inherited.departmentName) && (
          <Stack spacing={1.5}>
            {(inherited.programName || inherited.programPurpose) && (
              <InheritedBlock
                label={t('templateEditor.inherited.fromProgram')}
                hasContent={Boolean(inherited.programName || inherited.programPurpose)}
                emptyHint={t('templateEditor.inherited.emptyProgram')}
              >
                <Typography variant="body2" fontWeight={600} color="text.secondary">
                  {inherited.programName}
                </Typography>
                {inherited.programPurpose && (
                  <Typography variant="caption" color="text.secondary">
                    {inherited.programPurpose}
                  </Typography>
                )}
              </InheritedBlock>
            )}
            {(inherited.departmentName || inherited.departmentPurpose) && (
              <InheritedBlock
                label={t('templateEditor.inherited.fromDepartment')}
                hasContent={Boolean(inherited.departmentName || inherited.departmentPurpose)}
                emptyHint={t('templateEditor.inherited.emptyDepartment')}
              >
                <Typography variant="body2" fontWeight={600} color="text.secondary">
                  {inherited.departmentName}
                </Typography>
                {inherited.departmentPurpose && (
                  <Typography variant="caption" color="text.secondary">
                    {inherited.departmentPurpose}
                  </Typography>
                )}
              </InheritedBlock>
            )}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}

function DescriptionSection({ state, setState, inherited, promote }: SectionProps): JSX.Element {
  const { t } = useTranslation('sis');
  const courseLinked = Boolean(state.courseId);
  const inheritedText = courseLinked ? tiptapToPlainText(inherited?.description) : '';
  const draftDescription = tiptapToPlainText(state.description);
  // Course-owned: when the linked Course supplies a description it is shown
  // read-only (the syllabus renders the Course's). When the Course has none, the
  // template field is what the syllabus renders, so it stays editable — matching
  // the PDF renderer's `composed.description ?? template.description` fallback.
  const courseProvidesDescription = courseLinked && inheritedText.length > 0;
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack spacing={1.5}>
        {courseProvidesDescription ? (
          <InheritedBlock
            label={t('templateEditor.inherited.description')}
            hasContent={inheritedText.length > 0}
            emptyHint={t('templateEditor.inherited.emptyCourse')}
          >
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
              {inheritedText}
            </Typography>
          </InheritedBlock>
        ) : (
          <>
            <Typography variant="caption" color="text.secondary" display="block">
              {t('templateEditor.description.richTextHint')}
            </Typography>
            <PageEditor
              value={state.description}
              onChange={(v) => setState({ ...state, description: v })}
            />
            {promote?.courseId && (
              <Box>
                <PromoteToSource
                  entityLabel={t('templateEditor.promote.course')}
                  shared={false}
                  disabled={!draftDescription}
                  onConfirm={async () => {
                    await academicApi.updateCourse({
                      tenantId: promote.tenantId,
                      courseId: promote.courseId as string,
                      description: draftDescription,
                    });
                    promote.onDone();
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Stack>
    </Paper>
  );
}

function ObjectivesSection({ state, setState, inherited, promote }: SectionProps): JSX.Element {
  const { t } = useTranslation('sis');
  const inhProgram = inherited?.programObjectives ?? [];
  const inhDepartment = inherited?.departmentObjectives ?? [];
  const inhCourse = inherited?.courseObjectives ?? [];
  // Each objective level is shown read-only when its source (Program / Department
  // / Course) supplies content, and editable otherwise — matching the PDF
  // renderer's `composed.* ?? template.objectives.*` fallback. Fill the maintainer
  // to make a level read-only and have it update with the selection.
  const update = (key: keyof TemplateState['objectives'], items: { id: string; text: string }[]) =>
    setState({ ...state, objectives: { ...state.objectives, [key]: items } });

  const renderList = (
    key: keyof TemplateState['objectives'],
    label: string,
    items: { id: string; text: string }[],
    disabled = false,
  ) => (
    <Box sx={{ opacity: disabled ? 0.6 : 1 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle2" fontWeight={700}>
          {label}
        </Typography>
        <Button
          size="small"
          startIcon={<AddRounded />}
          disabled={disabled}
          onClick={() => update(key, [...items, { id: uid(), text: '' }])}
        >
          Agregar
        </Button>
      </Stack>
      <Stack spacing={1}>
        {items.map((o, i) => (
          <Stack key={o.id} direction="row" spacing={1}>
            <TextField
              size="small"
              fullWidth
              value={o.text}
              disabled={disabled}
              onChange={(e) =>
                update(
                  key,
                  items.map((it, j) => (j === i ? { ...it, text: e.target.value } : it)),
                )
              }
            />
            <IconButton
              color="error"
              disabled={disabled}
              onClick={() =>
                update(
                  key,
                  items.filter((_, j) => j !== i),
                )
              }
            >
              <DeleteOutlineRounded />
            </IconButton>
          </Stack>
        ))}
      </Stack>
    </Box>
  );

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack spacing={2}>
        {inhProgram.length > 0 ? (
          <InheritedBlock label={t('templateEditor.inherited.oap')} hasContent emptyHint="">
            <InheritedObjectives items={inhProgram} />
          </InheritedBlock>
        ) : (
          <Box>
            {renderList('program', t('templateEditor.inherited.oap'), state.objectives.program)}
            {promote?.programId && (
              <Box mt={1}>
                <PromoteToSource
                  entityLabel={t('templateEditor.promote.program')}
                  shared
                  disabled={toObjectiveDtos(state.objectives.program).length === 0}
                  onConfirm={async () => {
                    await academicApi.updateProgram({
                      tenantId: promote.tenantId,
                      programId: promote.programId as string,
                      learningObjectives: toObjectiveDtos(state.objectives.program),
                    });
                    promote.onDone();
                  }}
                />
              </Box>
            )}
          </Box>
        )}
        <Divider />
        {inhDepartment.length > 0 ? (
          <InheritedBlock label={t('templateEditor.inherited.oad')} hasContent emptyHint="">
            <InheritedObjectives items={inhDepartment} />
          </InheritedBlock>
        ) : (
          <Box>
            {renderList(
              'department',
              t('templateEditor.inherited.oad'),
              state.objectives.department,
            )}
            {promote?.departmentId && (
              <Box mt={1}>
                <PromoteToSource
                  entityLabel={t('templateEditor.promote.department')}
                  shared
                  disabled={toObjectiveDtos(state.objectives.department).length === 0}
                  onConfirm={async () => {
                    await academicApi.updateDepartment({
                      tenantId: promote.tenantId,
                      departmentId: promote.departmentId as string,
                      learningObjectives: toObjectiveDtos(state.objectives.department),
                    });
                    promote.onDone();
                  }}
                />
              </Box>
            )}
          </Box>
        )}
        <Divider />
        {inhCourse.length > 0 ? (
          <InheritedBlock label={t('templateEditor.inherited.oac')} hasContent emptyHint="">
            <InheritedObjectives items={inhCourse} />
          </InheritedBlock>
        ) : (
          <Box>
            {renderList('course', t('templateEditor.inherited.oac'), state.objectives.course)}
            {promote?.courseId && (
              <Box mt={1}>
                <PromoteToSource
                  entityLabel={t('templateEditor.promote.course')}
                  shared={false}
                  disabled={toObjectiveDtos(state.objectives.course).length === 0}
                  onConfirm={async () => {
                    await academicApi.updateCourse({
                      tenantId: promote.tenantId,
                      courseId: promote.courseId as string,
                      learningObjectives: toObjectiveDtos(state.objectives.course),
                    });
                    promote.onDone();
                  }}
                />
              </Box>
            )}
          </Box>
        )}
      </Stack>
    </Paper>
  );
}

function PhilosophySection({ state, setState, inherited, promote }: SectionProps): JSX.Element {
  const { t } = useTranslation('sis');
  const update = (items: TemplateState['philosophy']) => setState({ ...state, philosophy: items });
  const inhPhilosophy = inherited?.philosophy ?? [];
  const cleanPhilosophy = state.philosophy
    .filter((p) => p.title.trim() || p.description.trim())
    .map((p) => ({ id: p.id, title: p.title.trim(), description: p.description.trim() }));
  // Program-owned: read-only when the linked Program supplies philosophy, editable
  // otherwise — matching the PDF's `composed.philosophy ?? template.philosophy`.
  if (inhPhilosophy.length > 0) {
    return (
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <InheritedBlock label={t('templateEditor.inherited.philosophy')} hasContent emptyHint="">
          <Stack spacing={1}>
            {inhPhilosophy.map((p, i) => (
              <Box key={i}>
                <Typography variant="body2" fontWeight={600} color="text.secondary">
                  {p.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {p.description}
                </Typography>
              </Box>
            ))}
          </Stack>
        </InheritedBlock>
      </Paper>
    );
  }
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle2" fontWeight={700}>
          {t('templateEditor.philosophy.courseCommitments')}
        </Typography>
        <Button
          size="small"
          startIcon={<AddRounded />}
          onClick={() => update([...state.philosophy, { id: uid(), title: '', description: '' }])}
        >
          Agregar
        </Button>
      </Stack>
      <Stack spacing={2}>
        {state.philosophy.map((c, i) => (
          <Paper key={c.id} variant="outlined" sx={{ p: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Box sx={{ flex: 1 }}>
                <TextField
                  label="Título"
                  size="small"
                  fullWidth
                  value={c.title}
                  onChange={(e) =>
                    update(
                      state.philosophy.map((it, j) =>
                        j === i ? { ...it, title: e.target.value } : it,
                      ),
                    )
                  }
                />
                <TextField
                  label="Descripción"
                  size="small"
                  fullWidth
                  multiline
                  rows={2}
                  sx={{ mt: 1 }}
                  value={c.description}
                  onChange={(e) =>
                    update(
                      state.philosophy.map((it, j) =>
                        j === i ? { ...it, description: e.target.value } : it,
                      ),
                    )
                  }
                />
              </Box>
              <IconButton
                color="error"
                onClick={() => update(state.philosophy.filter((_, j) => j !== i))}
              >
                <DeleteOutlineRounded />
              </IconButton>
            </Stack>
          </Paper>
        ))}
        {promote?.programId && (
          <Box>
            <PromoteToSource
              entityLabel={t('templateEditor.promote.program')}
              shared
              disabled={cleanPhilosophy.length === 0}
              onConfirm={async () => {
                await academicApi.updateProgram({
                  tenantId: promote.tenantId,
                  programId: promote.programId as string,
                  philosophy: cleanPhilosophy,
                });
                promote.onDone();
              }}
            />
          </Box>
        )}
      </Stack>
    </Paper>
  );
}

/**
 * A collapsible optional rich-text note. Collapsed, it renders as a dashed
 * "field-like" bar (icon + title + "click to write" hint) so users clearly see
 * there's an editable description hidden; expanded, it shows a caption + editor.
 */
function CollapsibleNote({
  title,
  caption,
  open,
  onToggle,
  hasContent,
  children,
}: {
  title: string;
  caption: string;
  open: boolean;
  onToggle: () => void;
  hasContent: boolean;
  children: React.ReactNode;
}): JSX.Element {
  const { t } = useTranslation('sis');
  return (
    <Box>
      <Paper
        variant="outlined"
        onClick={onToggle}
        sx={{
          px: 1.5,
          py: 1,
          borderRadius: 2,
          borderStyle: 'dashed',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          <EditNoteRounded fontSize="small" sx={{ color: 'text.secondary' }} />
          <Typography variant="subtitle2" fontWeight={700} noWrap>
            {title}
          </Typography>
          {hasContent && !open && (
            <Chip
              size="small"
              label={t('templateEditor.noteFilled')}
              color="success"
              variant="outlined"
            />
          )}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
          <Typography variant="caption" color="text.secondary">
            {open ? t('templateEditor.noteHide') : t('templateEditor.noteShow')}
          </Typography>
          {open ? <ExpandLessRounded fontSize="small" /> : <ExpandMoreRounded fontSize="small" />}
        </Stack>
      </Paper>
      <Collapse in={open} unmountOnExit={false}>
        <Stack spacing={1} sx={{ pt: 1.5, px: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {caption}
          </Typography>
          {children}
        </Stack>
      </Collapse>
    </Box>
  );
}

/**
 * "Evaluaciones" — la pestaña de Evaluación, en maestro-detalle.
 *
 * LA EVALUACIÓN ES LA UNIDAD. En el documento real la misma "Ensayo de
 * investigación" que pesa 35 % es la que se describe en "Detalles de la tarea",
 * la que ocupa una fila en "Distribución de la tarea" y la que se entrega en el
 * calendario. Antes la descripción colgaba de un enum de seis tipos, y ese enum
 * no podía expresar dos ensayos distintos —el práctico y el de investigación—
 * ni "Lecturas", que es el 25 % de la nota y no es ninguno de los seis.
 *
 * MAESTRO-DETALLE Y NO UNA TARJETA POR EVALUACIÓN: así hay UN solo editor de
 * texto montado a la vez. Con tarjetas, un curso de seis evaluaciones monta seis
 * instancias de TipTap en la misma pantalla, y cada una trae su propio
 * ProseMirror con sus listeners.
 *
 * Las casillas de OAC SON la matriz de distribución: la matriz es una vista de
 * estas aristas, no una segunda tabla que haya que mantener igual.
 */
function EvaluationsMasterDetail({ state, setState, inherited }: SectionProps): JSX.Element {
  const { t } = useTranslation('sis');
  const components = state.grading.components;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // La selección es el id, no el índice: borrar una evaluación de más arriba
  // correría el índice y dejaría el detalle mostrando otra.
  const current = components.find((c) => c.id === selectedId) ?? components[0] ?? null;
  const totalWeight = components.reduce((sum, c) => sum + (c.weight ?? 0), 0);

  // Los OAC EFECTIVOS, con la misma precedencia que usa el renderer
  // (`composed.courseObjectives ?? template.objectives.course`): leer sólo la
  // lista de la plantilla no mostraría nada justo cuando el curso vinculado es
  // el que aporta los OAC, que es el caso normal.
  const objectives =
    inherited?.courseObjectives && inherited.courseObjectives.length > 0
      ? inherited.courseObjectives
      : state.objectives.course;

  const update = (next: TemplateState['grading']['components']) =>
    setState({ ...state, grading: { ...state.grading, components: next } });
  const patch = (id: string, fields: Partial<TemplateState['grading']['components'][number]>) =>
    update(components.map((c) => (c.id === id ? { ...c, ...fields } : c)));

  const add = () => {
    const id = uid();
    update([...components, { id, name: '', weight: 0 }]);
    setSelectedId(id);
  };

  const remove = (id: string) => {
    update(components.filter((c) => c.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  return (
    <Stack spacing={1.5}>
      <Box>
        <Typography variant="subtitle2" fontWeight={700}>
          {t('templateEditor.grading.componentsTitle')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t('templateEditor.grading.componentsCaption')}
        </Typography>
      </Box>

      {totalWeight !== 100 && components.length > 0 && (
        <Alert severity="warning" variant="outlined">
          {t('templateEditor.grading.weightMustSum', { total: totalWeight })}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
        {/* Maestro */}
        <Paper
          variant="outlined"
          sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0, borderRadius: 2 }}
        >
          <List dense disablePadding>
            {components.length === 0 && (
              <Box sx={{ p: 2 }}>
                <Typography variant="caption" color="text.disabled">
                  {t('templateEditor.grading.componentsEmpty')}
                </Typography>
              </Box>
            )}
            {components.map((c) => (
              <ListItemButton
                key={c.id}
                selected={current?.id === c.id}
                onClick={() => setSelectedId(c.id)}
              >
                <ListItemText
                  primary={c.name.trim() || t('templateEditor.grading.unnamed')}
                  secondary={c.hours ? `${c.weight ?? 0} % · ${c.hours} h` : `${c.weight ?? 0} %`}
                  slotProps={{
                    primary: { variant: 'body2', noWrap: true },
                    secondary: { variant: 'caption' },
                  }}
                />
                {/* Una marca discreta de que esa evaluación ya tiene su descripción:
                    sin ella, saber qué falta obliga a entrar a cada una. */}
                {c.description && <EditNoteRounded fontSize="small" color="disabled" />}
              </ListItemButton>
            ))}
          </List>
          <Divider />
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ px: 1.5, py: 1 }}
          >
            <Typography
              variant="caption"
              fontWeight={700}
              color={totalWeight === 100 ? 'success.main' : 'text.secondary'}
            >
              {t('templateEditor.grading.totalWeight', { total: totalWeight })}
            </Typography>
            <Button size="small" startIcon={<AddRounded />} onClick={add}>
              {t('templateEditor.grading.componentAdd')}
            </Button>
          </Stack>
        </Paper>

        {/* Detalle */}
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          {current === null ? (
            <Typography variant="body2" color="text.disabled">
              {t('templateEditor.grading.selectHint')}
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <TextField
                  size="small"
                  label={t('templateEditor.grading.componentName')}
                  value={current.name}
                  onChange={(e) => patch(current.id, { name: e.target.value })}
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  type="number"
                  label={t('templateEditor.grading.componentWeight')}
                  value={current.weight}
                  onChange={(e) => patch(current.id, { weight: Number(e.target.value) })}
                  sx={{ width: 100 }}
                />
                {/* Las horas van AQUÍ, pegadas al peso: es la misma pregunta
                    —cuánto vale y cuánto cuesta este trabajo— y así la tabla de
                    horas del documento se llena mientras se declara la
                    evaluación, no en una segunda pasada por otra pantalla. */}
                <TextField
                  size="small"
                  type="number"
                  label={t('templateEditor.grading.componentHours')}
                  value={current.hours ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value.trim();
                    update(
                      components.map((c) => {
                        if (c.id !== current.id) return c;
                        // Vaciar el campo BORRA la clave, no la deja en cero ni
                        // en `undefined`: cero afirmaría que el trabajo no lleva
                        // tiempo, y un `undefined` explícito lo rechaza Firestore.
                        if (raw === '') {
                          const { hours: _cleared, ...rest } = c;
                          return rest;
                        }
                        return { ...c, hours: Number(raw) };
                      }),
                    );
                  }}
                  sx={{ width: 100 }}
                />
                <IconButton
                  color="error"
                  aria-label={t('templateEditor.grading.componentDelete')}
                  onClick={() => remove(current.id)}
                >
                  <DeleteOutlineRounded />
                </IconButton>
              </Stack>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('templateEditor.grading.descriptionHelp')}
                </Typography>
                {/* `key` fuerza el remontaje al cambiar de evaluación: TipTap no
                    re-lee `value` una vez montado, así que sin esto el detalle
                    seguiría mostrando el texto de la evaluación anterior. */}
                <PageEditor
                  key={current.id}
                  value={current.description ?? null}
                  onChange={(json) => patch(current.id, { description: json })}
                  placeholder={t('templateEditor.grading.descriptionPlaceholder')}
                />
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('templateEditor.grading.contributesTo')}
                </Typography>
                {objectives.length === 0 ? (
                  <Typography variant="caption" display="block" color="text.secondary">
                    {t('templateEditor.grading.noObjectives')}
                  </Typography>
                ) : (
                  <FormGroup>
                    {objectives.map((o, idx) => (
                      <FormControlLabel
                        key={o.id}
                        control={
                          <Checkbox
                            size="small"
                            checked={(current.objectiveIds ?? []).includes(o.id)}
                            onChange={(e) =>
                              patch(current.id, {
                                objectiveIds: e.target.checked
                                  ? [...(current.objectiveIds ?? []), o.id]
                                  : (current.objectiveIds ?? []).filter((x) => x !== o.id),
                              })
                            }
                          />
                        }
                        label={
                          <Typography variant="body2">
                            <strong>
                              {t('templateEditor.grading.objectiveCode', { n: idx + 1 })}
                            </strong>{' '}
                            {o.text}
                          </Typography>
                        }
                      />
                    ))}
                  </FormGroup>
                )}
              </Box>
            </Stack>
          )}
        </Box>
      </Stack>
    </Stack>
  );
}

function GradingSection({ state, setState, inherited }: SectionProps): JSX.Element {
  const { t } = useTranslation('sis');
  const [noteOpen, setNoteOpen] = useState(false);
  const update = (g: TemplateState['grading']) => setState({ ...state, grading: g });
  // Las dos listas, igual que la tabla impresa: las horas que declara cada
  // evaluación y la dedicación que no se califica. Un total que sólo contara una
  // de las dos sería un número que no aparece en ninguna parte del documento.
  const { total: totalHours } = buildWorkloadRows(
    state.grading.components,
    state.grading.expectedHours,
  );
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack spacing={2}>
        {/* Collapsible optional evaluation description — starts collapsed to cut scroll. */}
        <CollapsibleNote
          title={t('templateEditor.grading.noteTitle')}
          caption={t('templateEditor.grading.noteCaption')}
          open={noteOpen}
          onToggle={() => setNoteOpen((o) => !o)}
          hasContent={Boolean(state.grading.note)}
        >
          <PageEditor
            value={state.grading.note ?? null}
            onChange={(json) => update({ ...state.grading, note: json })}
            placeholder={t('templateEditor.grading.notePlaceholder')}
          />
        </CollapsibleNote>
        <Divider />
        <EvaluationsMasterDetail
          state={state}
          setState={setState}
          {...(inherited === undefined ? {} : { inherited })}
        />
        <Divider />
        <TextField
          label={t('templateEditor.grading.passingGrade')}
          size="small"
          type="number"
          value={state.grading.passingGrade}
          onChange={(e) => update({ ...state.grading, passingGrade: Number(e.target.value) })}
          sx={{ width: 160 }}
        />
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2" fontWeight={700}>
            {t('templateEditor.grading.hoursTitle')}
          </Typography>
          <Button
            size="small"
            startIcon={<AddRounded />}
            onClick={() =>
              update({
                ...state.grading,
                expectedHours: [...state.grading.expectedHours, { id: uid(), label: '', hours: 0 }],
              })
            }
          >
            {t('templateEditor.grading.hoursAdd')}
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {t('templateEditor.grading.hoursCaption')}
        </Typography>
        <Stack spacing={1}>
          {state.grading.expectedHours.length === 0 && (
            <Typography variant="caption" color="text.disabled">
              {t('templateEditor.grading.hoursEmpty')}
            </Typography>
          )}
          {state.grading.expectedHours.map((h, i) => (
            <Stack key={h.id} direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                placeholder={t('templateEditor.grading.hoursItemLabel')}
                value={h.label}
                onChange={(e) =>
                  update({
                    ...state.grading,
                    expectedHours: state.grading.expectedHours.map((it, j) =>
                      j === i ? { ...it, label: e.target.value } : it,
                    ),
                  })
                }
                sx={{ flex: 2 }}
              />
              <TextField
                size="small"
                type="number"
                placeholder={t('templateEditor.grading.hoursItemHours')}
                value={h.hours}
                onChange={(e) =>
                  update({
                    ...state.grading,
                    expectedHours: state.grading.expectedHours.map((it, j) =>
                      j === i ? { ...it, hours: Number(e.target.value) } : it,
                    ),
                  })
                }
                sx={{ width: 110 }}
              />
              <IconButton
                color="error"
                onClick={() =>
                  update({
                    ...state.grading,
                    expectedHours: state.grading.expectedHours.filter((_, j) => j !== i),
                  })
                }
              >
                <DeleteOutlineRounded />
              </IconButton>
            </Stack>
          ))}
        </Stack>
        {totalHours > 0 && (
          <Typography variant="subtitle2" fontWeight={700} textAlign="right">
            {t('templateEditor.grading.hoursTotal', { hours: totalHours })}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}

/** Cómo se nombra un libro del catálogo en cualquier selector. Vive a nivel de
 *  módulo porque lo usan el selector de bibliografía y el plan de lecturas: dos
 *  formas de nombrar el mismo libro en la misma pantalla confunden al que carga. */
const bookLabel = (o: TextbookDto): string =>
  `${o.title}${o.authors.length ? ` — ${o.authors.join(', ')}` : ''}${o.year ? ` (${o.year})` : ''}`;

/**
 * Una lista de libros del catálogo, como chips en UN solo control.
 *
 * La bibliografía de un curso puede ser larga —la de TH706 son 73 libros— y la
 * versión anterior montaba un `Autocomplete` por libro, cada uno con el catálogo
 * completo detrás. Con chips hay un control por lista, se busca escribiendo y se
 * quita un libro con su ✕.
 *
 * `notes` se conserva: el modelo permite una nota por libro, ninguna pantalla la
 * edita hoy, y reescribir la lista sin cuidado borraría en silencio las que
 * hubiera cargado el importador de PDF.
 */
function BookPickerList({
  title,
  caption,
  textbooks,
  items,
  onChange,
}: {
  title: string;
  caption: string;
  textbooks: TextbookDto[];
  items: { textbookId: string; notes?: string }[];
  onChange: (items: { textbookId: string; notes?: string }[]) => void;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const byId = new Map(textbooks.map((tb) => [tb.id, tb]));
  const notesById = new Map(items.map((it) => [it.textbookId, it.notes]));
  // Un libro que ya no está en el catálogo (lo borraron) no puede ser un chip:
  // se cuenta aparte para que su ausencia no pase por una eliminación nuestra.
  const selected = items
    .map((it) => byId.get(it.textbookId))
    .filter((tb): tb is TextbookDto => !!tb);
  const missing = items.length - selected.length;

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={700}>
        {title} {items.length > 0 && `(${items.length})`}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" mb={1}>
        {caption}
      </Typography>
      <Autocomplete
        multiple
        size="small"
        disableCloseOnSelect
        options={textbooks}
        getOptionLabel={bookLabel}
        value={selected}
        onChange={(_, vals) =>
          onChange(
            vals.map((tb) => {
              const notes = notesById.get(tb.id);
              return notes === undefined ? { textbookId: tb.id } : { textbookId: tb.id, notes };
            }),
          )
        }
        isOptionEqualToValue={(o, v) => o.id === v.id}
        renderInput={(params) => (
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          <TextField
            {...(params as any)}
            size="small"
            label={t('templateEditor.readings.addBook')}
          />
        )}
      />
      {missing > 0 && (
        <Typography variant="caption" color="warning.main">
          {t('templateEditor.readings.missingBooks', { count: missing })}
        </Typography>
      )}
    </Box>
  );
}

function ReadingsSection({
  state,
  setState,
  textbooks,
}: SectionProps & { textbooks: TextbookDto[] }): JSX.Element {
  const update = (r: TemplateState['readings']) => setState({ ...state, readings: r });

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="caption" color="text.secondary">
          Selecciona libros del catálogo. Para crear, editar o eliminar libros usa el menú "Catálogo
          de libros" del seminario.
        </Typography>
        <Button
          size="small"
          variant="text"
          onClick={() => window.open('/catalogo-libros', '_blank')}
        >
          Ir al catálogo →
        </Button>
      </Stack>
      <ReadingsEditor readings={state.readings} update={update} textbooks={textbooks} />
    </Paper>
  );
}

// Exported for reuse by the /curso Syllabus tab's readings override dialog
// (F2): it edits a plain readings value, decoupled from the template editor
// state. Same UI in both surfaces.
export function ReadingsEditor({
  readings,
  update,
  textbooks,
}: {
  readings: TemplateState['readings'];
  update: (r: TemplateState['readings']) => void;
  textbooks: TextbookDto[];
}): JSX.Element {
  const { t } = useTranslation('sis');

  const planRows = readings.plan?.rows ?? [];
  const planTotal = planRows.reduce((acc, r) => acc + (Number(r.pages) || 0), 0);

  const ensurePlan = (): NonNullable<TemplateState['readings']['plan']> =>
    readings.plan ?? { rows: [] };

  /** Set or CLEAR the week of one plan row.
   *
   *  Clearing drops the key rather than writing `undefined`: the calendar treats
   *  "no week" as its own bucket, and a row that stored 0 would instead be
   *  scheduled in the first week — a due date nobody chose. */
  const setRowWeek = (index: number, weekOffset: number | undefined) =>
    update({
      ...readings,
      plan: {
        ...ensurePlan(),
        rows: planRows.map((rr, j) => {
          if (j !== index) return rr;
          const { weekOffset: _previous, ...rest } = rr;
          return weekOffset === undefined ? rest : { ...rest, weekOffset };
        }),
      },
    });

  return (
    <Stack spacing={3.5}>
      {/* ── Plan de lecturas ─────────────────────────────────────────── */}
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Box>
            <Typography variant="subtitle2" fontWeight={700}>
              {t('templateEditor.readings.planTitle')}{' '}
              {planRows.length > 0 && t('templateEditor.readings.planTotal', { count: planTotal })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('templateEditor.readings.planHelp')}
            </Typography>
          </Box>
          <Button
            size="small"
            startIcon={<AddRounded />}
            onClick={() =>
              update({
                ...readings,
                plan: {
                  ...ensurePlan(),
                  rows: [...(readings.plan?.rows ?? []), { id: uid(), label: '', pages: 0 }],
                },
              })
            }
          >
            {t('templateEditor.readings.addRow')}
          </Button>
        </Stack>
        {planRows.length > 0 && (
          <Stack spacing={1.5}>
            <TextField
              size="small"
              label={t('templateEditor.readings.introNote')}
              placeholder={t('templateEditor.readings.introNotePlaceholder')}
              value={readings.plan?.introNote ?? ''}
              onChange={(e) =>
                update({
                  ...readings,
                  plan: { ...ensurePlan(), introNote: e.target.value },
                })
              }
              fullWidth
            />
            <TextField
              size="small"
              label={t('templateEditor.readings.description')}
              placeholder={t('templateEditor.readings.descriptionPlaceholder')}
              value={readings.plan?.description ?? ''}
              onChange={(e) =>
                update({
                  ...readings,
                  plan: { ...ensurePlan(), description: e.target.value },
                })
              }
              multiline
              minRows={2}
              fullWidth
            />
            {planRows.map((row, i) => (
              <Stack key={row.id} direction="row" spacing={1.5} alignItems="center">
                <TextField
                  size="small"
                  label={t('templateEditor.readings.rowLabel')}
                  placeholder={t('templateEditor.readings.rowLabelPlaceholder')}
                  value={row.label}
                  onChange={(e) =>
                    update({
                      ...readings,
                      plan: {
                        ...ensurePlan(),
                        rows: planRows.map((rr, j) =>
                          j === i ? { ...rr, label: e.target.value } : rr,
                        ),
                      },
                    })
                  }
                  sx={{ flex: 2 }}
                />
                <Autocomplete
                  size="small"
                  options={textbooks}
                  getOptionLabel={bookLabel}
                  value={textbooks.find((tb) => tb.id === row.textbookId) ?? null}
                  onChange={(_, val) =>
                    update({
                      ...readings,
                      plan: {
                        ...ensurePlan(),
                        rows: planRows.map((rr, j) =>
                          j === i ? { ...rr, textbookId: val?.id ?? '' } : rr,
                        ),
                      },
                    })
                  }
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  renderInput={(params) => (
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    <TextField
                      {...(params as any)}
                      size="small"
                      label={t('templateEditor.readings.bookOptional')}
                    />
                  )}
                  sx={{ flex: 1.5 }}
                />
                <TextField
                  size="small"
                  type="number"
                  label={t('templateEditor.readings.week')}
                  placeholder={t('templateEditor.readings.weekPlaceholder')}
                  helperText={t('templateEditor.readings.weekHelp')}
                  // Shown 1-based, stored 0-based — same convention as the
                  // assignments' "Sem #". Empty means "not placed yet", which the
                  // calendar renders in its own group instead of week 1.
                  value={row.weekOffset === undefined ? '' : row.weekOffset + 1}
                  onChange={(e) => {
                    const raw = e.target.value.trim();
                    const parsed = Number(raw);
                    // Integers only: 1.5 would store weekOffset 0.5 and give the
                    // calendar a phantom half-week bucket of its own.
                    setRowWeek(
                      i,
                      raw === '' || !Number.isInteger(parsed) || parsed < 1
                        ? undefined
                        : parsed - 1,
                    );
                  }}
                  sx={{ width: 104 }}
                />
                <TextField
                  size="small"
                  type="number"
                  label={t('templateEditor.readings.pages')}
                  value={row.pages}
                  onChange={(e) =>
                    update({
                      ...readings,
                      plan: {
                        ...ensurePlan(),
                        rows: planRows.map((rr, j) =>
                          j === i ? { ...rr, pages: Number(e.target.value) } : rr,
                        ),
                      },
                    })
                  }
                  sx={{ width: 110 }}
                />
                <IconButton
                  color="error"
                  onClick={() =>
                    update({
                      ...readings,
                      plan: {
                        ...ensurePlan(),
                        rows: planRows.filter((_, j) => j !== i),
                      },
                    })
                  }
                >
                  <DeleteOutlineRounded />
                </IconButton>
              </Stack>
            ))}
            <TextField
              size="small"
              label={t('templateEditor.readings.closingNote')}
              placeholder={t('templateEditor.readings.closingNotePlaceholder')}
              value={readings.plan?.closingNote ?? ''}
              onChange={(e) =>
                update({
                  ...readings,
                  plan: { ...ensurePlan(), closingNote: e.target.value },
                })
              }
              multiline
              minRows={2}
              fullWidth
            />
          </Stack>
        )}
      </Box>

      <Divider />

      {/* ── Bibliografía ──────────────────────────────────────────────────
          Un solo control por lista, con los libros como chips. Antes cada libro
          era un `Autocomplete` propio: la bibliografía del plan de TH706 son 73
          libros, o sea 73 autocompletes montados, cada uno con el catálogo
          entero detrás. Es el mismo error que la pestaña Evaluación tenía con
          los editores de texto.

          Y va DESPUÉS del plan de lecturas: la bibliografía casi no se edita
          —se carga una vez y se consulta—, mientras que el plan se toca en cada
          revisión y es el que alimenta el calendario del curso. */}
      <BookPickerList
        title={t('templateEditor.readings.requiredTitle')}
        caption={t('templateEditor.readings.requiredCaption')}
        textbooks={textbooks}
        items={readings.required}
        onChange={(items) => update({ ...readings, required: items })}
      />

      <Divider />

      <Box>
        <BookPickerList
          title={t('templateEditor.readings.complementaryTitle')}
          caption={t('templateEditor.readings.complementaryCaption')}
          textbooks={textbooks}
          items={readings.complementary.items}
          onChange={(items) =>
            update({ ...readings, complementary: { ...readings.complementary, items } })
          }
        />
        <TextField
          size="small"
          label={t('templateEditor.readings.complementaryIntro')}
          placeholder={t('templateEditor.readings.complementaryIntroPlaceholder')}
          value={readings.complementary.intro ?? ''}
          onChange={(e) =>
            update({
              ...readings,
              complementary: { ...readings.complementary, intro: e.target.value },
            })
          }
          multiline
          minRows={2}
          fullWidth
          sx={{ mt: 1.5 }}
        />
      </Box>
    </Stack>
  );
}

type WeekdayTplState = TemplateState['sessionFormat']['weekdayTemplates'][number];
type SegmentState = WeekdayTplState['segments'][number];

function SessionFormatSection({ state, setState }: SectionProps): JSX.Element {
  // Preserve introNote on every weekdayTemplates mutation — callers only pass
  // `{ weekdayTemplates }`, so without this merge the description would be
  // dropped on the next day/segment edit.
  const update = (sf: { weekdayTemplates: TemplateState['sessionFormat']['weekdayTemplates'] }) =>
    setState({
      ...state,
      sessionFormat: {
        ...(state.sessionFormat.introNote !== undefined
          ? { introNote: state.sessionFormat.introNote }
          : {}),
        weekdayTemplates: sf.weekdayTemplates,
      },
    });
  const updateDay = (i: number, patch: Partial<WeekdayTplState>) =>
    update({
      weekdayTemplates: state.sessionFormat.weekdayTemplates.map((it, j) =>
        j === i ? { ...it, ...patch } : it,
      ),
    });

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack spacing={2.5}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="subtitle2" fontWeight={700}>
              Días y horarios típicos del curso
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Define inicio + fin, luego agrega bloques de clase y recesos. El sistema calcula las
              horas reales y avisa si los minutos no cuadran con la duración total.
            </Typography>
          </Box>
          <Button
            size="small"
            startIcon={<AddRounded />}
            onClick={() =>
              update({
                weekdayTemplates: [
                  ...state.sessionFormat.weekdayTemplates,
                  {
                    id: uid(),
                    weekday: 'friday',
                    startTime: '20:00',
                    endTime: '23:00',
                    segments: [
                      { id: uid(), kind: 'section', durationMinutes: 90 },
                      { id: uid(), kind: 'break', durationMinutes: 15 },
                      { id: uid(), kind: 'section', durationMinutes: 75 },
                    ],
                  },
                ],
              })
            }
          >
            Agregar día
          </Button>
        </Stack>
        <TextField
          size="small"
          label="Descripción del formato (opcional)"
          placeholder="Cada jornada se organiza en secciones de tiempo fijas separadas por recesos."
          value={
            state.sessionFormat.introNote ??
            'Cada jornada se organiza en secciones de tiempo fijas separadas por recesos.'
          }
          onChange={(e) =>
            setState({
              ...state,
              sessionFormat: {
                weekdayTemplates: state.sessionFormat.weekdayTemplates,
                introNote: e.target.value,
              },
            })
          }
          multiline
          minRows={2}
          fullWidth
        />
        {state.sessionFormat.weekdayTemplates.map((w, i) => (
          <WeekdayEditor
            key={w.id}
            day={w}
            onChange={(patch) => updateDay(i, patch)}
            onDelete={() =>
              update({
                weekdayTemplates: state.sessionFormat.weekdayTemplates.filter((_, j) => j !== i),
              })
            }
          />
        ))}
      </Stack>
    </Paper>
  );
}

function WeekdayEditor({
  day,
  onChange,
  onDelete,
}: {
  day: WeekdayTplState;
  onChange: (patch: Partial<WeekdayTplState>) => void;
  onDelete: () => void;
}): JSX.Element {
  const totalTarget = Math.max(0, clockToMinSafe(day.endTime) - clockToMinSafe(day.startTime));
  const totalActual = day.segments.reduce((a, s) => a + (Number(s.durationMinutes) || 0), 0);
  const delta = totalActual - totalTarget;
  const healthOk = delta === 0 && totalTarget > 0;
  const healthLabel = healthOk
    ? `✓ ${totalActual} min calzan con el horario`
    : delta > 0
      ? `⚠ Sobran ${delta} min (suman ${totalActual}, el día dura ${totalTarget})`
      : `⚠ Faltan ${-delta} min (suman ${totalActual}, el día dura ${totalTarget})`;

  // Compute running clock for each segment
  let cursor = 0;
  const rows = day.segments.map((seg, idx) => {
    const startClock = addMinutesToClock(day.startTime, cursor);
    cursor += Number(seg.durationMinutes) || 0;
    const endClock = addMinutesToClock(day.startTime, cursor);
    const sectionNumber = day.segments.slice(0, idx + 1).filter((s) => s.kind === 'section').length;
    const breakNumber = day.segments.slice(0, idx + 1).filter((s) => s.kind === 'break').length;
    return { seg, idx, startClock, endClock, sectionNumber, breakNumber };
  });

  const addSegment = (kind: 'section' | 'break') =>
    onChange({
      segments: [
        ...day.segments,
        { id: uid(), kind, durationMinutes: kind === 'section' ? 90 : 15 },
      ],
    });
  const updateSeg = (idx: number, patch: Partial<SegmentState>) =>
    onChange({
      segments: day.segments.map((s, j) => (j === idx ? { ...s, ...patch } : s)),
    });
  const removeSeg = (idx: number) =>
    onChange({ segments: day.segments.filter((_, j) => j !== idx) });
  const moveSeg = (idx: number, dir: -1 | 1) => {
    const next = [...day.segments];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target]!, next[idx]!];
    onChange({ segments: next });
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <Select
              value={day.weekday}
              onChange={(e) => onChange({ weekday: e.target.value as Weekday })}
            >
              {WEEKDAYS.map((d) => (
                <MenuItem key={d.value} value={d.value}>
                  {d.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Inicio"
            value={day.startTime}
            onChange={(e) => onChange({ startTime: e.target.value })}
            placeholder="HH:MM"
            sx={{ width: 110 }}
          />
          <TextField
            size="small"
            label="Fin"
            value={day.endTime}
            onChange={(e) => onChange({ endTime: e.target.value })}
            placeholder="HH:MM"
            sx={{ width: 110 }}
          />
          <Box sx={{ flex: 1 }} />
          <Chip
            size="small"
            label={healthLabel}
            color={healthOk ? 'success' : 'warning'}
            variant="outlined"
          />
          <IconButton color="error" onClick={onDelete}>
            <DeleteOutlineRounded />
          </IconButton>
        </Stack>

        <Box>
          <Stack direction="row" spacing={1} mb={1}>
            <Button size="small" variant="outlined" onClick={() => addSegment('section')}>
              + Bloque de clase
            </Button>
            <Button size="small" variant="outlined" onClick={() => addSegment('break')}>
              + Receso
            </Button>
          </Stack>
          <Stack spacing={1}>
            {rows.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                Aún no hay segmentos. Agrega un bloque o receso arriba.
              </Typography>
            )}
            {rows.map(({ seg, idx, startClock, endClock, sectionNumber, breakNumber }) => (
              <Stack
                key={seg.id}
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{
                  p: 1.25,
                  borderRadius: 1,
                  bgcolor:
                    seg.kind === 'section' ? 'rgba(99,102,241,0.06)' : 'rgba(245,158,11,0.06)',
                  border: '1px solid',
                  borderColor:
                    seg.kind === 'section' ? 'rgba(99,102,241,0.2)' : 'rgba(245,158,11,0.2)',
                }}
              >
                <Chip
                  size="small"
                  label={
                    seg.kind === 'section' ? `Bloque ${sectionNumber}` : `Receso ${breakNumber}`
                  }
                  color={seg.kind === 'section' ? 'primary' : 'warning'}
                  sx={{ minWidth: 100 }}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Minutos"
                  value={seg.durationMinutes}
                  onChange={(e) => updateSeg(idx, { durationMinutes: Number(e.target.value) })}
                  sx={{ width: 110 }}
                />
                <Typography variant="body2" sx={{ flex: 1, fontFamily: 'monospace' }}>
                  {startClock} → {endClock}
                </Typography>
                <TextField
                  size="small"
                  label="Etiqueta (opcional)"
                  placeholder={seg.kind === 'break' ? 'Café, almuerzo…' : 'Cristología…'}
                  value={seg.label ?? ''}
                  onChange={(e) => updateSeg(idx, { label: e.target.value })}
                  sx={{ width: 200 }}
                />
                <IconButton
                  size="small"
                  disabled={idx === 0}
                  onClick={() => moveSeg(idx, -1)}
                  title="Subir"
                >
                  <ChevronLeftRounded sx={{ transform: 'rotate(90deg)' }} fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={idx === day.segments.length - 1}
                  onClick={() => moveSeg(idx, 1)}
                  title="Bajar"
                >
                  <ChevronRightRounded sx={{ transform: 'rotate(90deg)' }} fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" onClick={() => removeSeg(idx)}>
                  <DeleteOutlineRounded fontSize="small" />
                </IconButton>
              </Stack>
            ))}
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}

function ModulesSection({ state, setState }: SectionProps): JSX.Element {
  const { t } = useTranslation('sis');
  const update = (modules: ModuleTpl[]) => setState({ ...state, modules });
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2" fontWeight={700}>
            {t('templateEditor.modules.title')}
          </Typography>
          <Button
            size="small"
            startIcon={<AddRounded />}
            onClick={() =>
              update([
                ...state.modules,
                {
                  id: uid(),
                  name: t('templateEditor.modules.newName'),
                  axisTopic: '',
                  weekOffsets: [],
                  dayOffsets: [],
                  sessions: [],
                },
              ])
            }
          >
            {t('templateEditor.modules.add')}
          </Button>
        </Stack>

        {state.modules.map((m, i) => (
          <ModuleEditor
            key={m.id}
            module={m}
            onChange={(patched) => update(state.modules.map((it, j) => (j === i ? patched : it)))}
            onDelete={() => update(state.modules.filter((_, j) => j !== i))}
            weekdayTemplates={state.sessionFormat.weekdayTemplates}
          />
        ))}
      </Stack>
    </Paper>
  );
}

/** «Día 2 — Sábado (semana 1)» y sus variantes cortas, para los selectores. */
function useModuleDayLabels(module: ModuleTpl) {
  const { t } = useTranslation('sis');
  const weekday = (w: Weekday) => t(`templateEditor.modules.weekdays.${w}`);
  return {
    full: (i: number) => {
      const d = module.dayOffsets[i]!;
      return t('templateEditor.modules.days.label', {
        n: i + 1,
        weekday: weekday(d.weekday),
        week: d.weekIndex + 1,
      });
    },
    short: (i: number) =>
      t('templateEditor.modules.days.option', {
        n: i + 1,
        weekday: weekday(module.dayOffsets[i]!.weekday).slice(0, 3),
      }),
    withWeek: (i: number) => {
      const d = module.dayOffsets[i]!;
      return t('templateEditor.modules.days.optionWithWeek', {
        n: i + 1,
        weekday: weekday(d.weekday).slice(0, 3),
        week: d.weekIndex + 1,
      });
    },
  };
}

function ModuleEditor({
  module,
  onChange,
  onDelete,
  weekdayTemplates,
}: {
  module: ModuleTpl;
  onChange: (m: ModuleTpl) => void;
  onDelete: () => void;
  weekdayTemplates: TemplateState['sessionFormat']['weekdayTemplates'];
}): JSX.Element {
  const { t } = useTranslation('sis');
  const k = 'templateEditor.modules';
  const labels = useModuleDayLabels(module);
  // S1: el día que se está por borrar, si tiene sesiones (si no, se borra directo).
  const [deletingDay, setDeletingDay] = useState<number | null>(null);

  const deleteDay = (day: number) => {
    if (sessionsOnDay(module, day) === 0) {
      onChange(deleteModuleDay(module, day, { kind: 'drop' }));
      return;
    }
    setDeletingDay(day);
  };

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 1.5 }}>
      <Stack spacing={2.5}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <TextField
            size="small"
            label={t(`${k}.name`)}
            value={module.name}
            onChange={(e) => onChange({ ...module, name: e.target.value })}
            sx={{ flex: 1 }}
          />
          <TextField
            size="small"
            label={t(`${k}.axisTopic`)}
            value={module.axisTopic}
            onChange={(e) => onChange({ ...module, axisTopic: e.target.value })}
            sx={{ flex: 2 }}
          />
          <IconButton color="error" onClick={onDelete} aria-label={t(`${k}.delete`)}>
            <DeleteOutlineRounded />
          </IconButton>
        </Stack>

        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Box>
              <Typography variant="caption" fontWeight={700} display="block">
                {t(`${k}.days.title`)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t(`${k}.days.hint`)}
              </Typography>
            </Box>
            <Button
              size="small"
              startIcon={<AddRounded />}
              onClick={() =>
                onChange({
                  ...module,
                  dayOffsets: [...module.dayOffsets, { weekIndex: 0, weekday: 'friday' }],
                })
              }
            >
              {t(`${k}.days.add`)}
            </Button>
          </Stack>
          <Stack spacing={1.5}>
            {module.dayOffsets.map((d, i) => (
              <Stack key={i} direction="row" spacing={1.5} alignItems="center">
                <TextField
                  size="small"
                  label={t(`${k}.days.week`)}
                  type="number"
                  // Shown 1-indexed to match the day label; stored 0-indexed.
                  value={d.weekIndex + 1}
                  onChange={(e) =>
                    onChange({
                      ...module,
                      dayOffsets: module.dayOffsets.map((it, j) =>
                        j === i
                          ? { ...it, weekIndex: Math.max(0, Number(e.target.value) - 1) }
                          : it,
                      ),
                    })
                  }
                  inputProps={{ min: 1 }}
                  sx={{ width: 110 }}
                />
                <FormControl size="small" sx={{ width: 150 }}>
                  <Select
                    value={d.weekday}
                    inputProps={{ 'aria-label': t(`${k}.days.weekday`) }}
                    onChange={(e) =>
                      onChange({
                        ...module,
                        dayOffsets: module.dayOffsets.map((it, j) =>
                          j === i ? { ...it, weekday: e.target.value as Weekday } : it,
                        ),
                      })
                    }
                  >
                    {WEEKDAYS.map((w) => (
                      <MenuItem key={w.value} value={w.value}>
                        {t(`${k}.weekdays.${w.value}`)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                  {labels.full(i)}
                </Typography>
                <IconButton
                  color="error"
                  aria-label={t(`${k}.days.delete`)}
                  title={t(`${k}.days.delete`)}
                  onClick={() => deleteDay(i)}
                >
                  <DeleteOutlineRounded />
                </IconButton>
              </Stack>
            ))}
          </Stack>
        </Box>

        <ModuleSessionsEditor
          module={module}
          onChange={onChange}
          weekdayTemplates={weekdayTemplates}
        />
      </Stack>

      <ModuleDayDeleteDialog
        open={deletingDay !== null}
        day={deletingDay ?? 0}
        sessionCount={deletingDay === null ? 0 : sessionsOnDay(module, deletingDay)}
        dayLabels={module.dayOffsets.map((_, i) => labels.full(i))}
        onCancel={() => setDeletingDay(null)}
        onConfirm={(plan) => {
          if (deletingDay !== null) onChange(deleteModuleDay(module, deletingDay, plan));
          setDeletingDay(null);
        }}
      />
    </Paper>
  );
}

type DayTimelineRow =
  | {
      kind: 'session';
      session: SessionTpl;
      sessionArrayIndex: number; // index in module.sessions
      dayPosition: number; // 0-based position within day
      dayCount: number; // total sessions for this day
      blockNumber: number;
      startClock: string;
      endClock: string;
      defaultDuration: number;
      overflow: boolean;
      /** S6: pidió más minutos de los que le quedaban al bloque. */
      truncated?: { actual: number; requested: number; belowMinimum: boolean };
    }
  | {
      kind: 'break';
      label: string;
      startClock: string;
      endClock: string;
      durationMinutes: number;
    };

/** S6: «recortada: 10 de 45 min». Por debajo de la mitad, en rojo. */
function TruncatedChip({
  truncated,
}: {
  truncated: { actual: number; requested: number; belowMinimum: boolean };
}): JSX.Element {
  const { t } = useTranslation('sis');
  return (
    <Chip
      size="small"
      color={truncated.belowMinimum ? 'error' : 'warning'}
      variant="outlined"
      label={t('templateEditor.modules.fit.chip', {
        actual: truncated.actual,
        requested: truncated.requested,
      })}
      sx={{ flexShrink: 0 }}
    />
  );
}

function ModuleSessionsEditor({
  module,
  onChange,
  weekdayTemplates,
}: {
  module: ModuleTpl;
  onChange: (m: ModuleTpl) => void;
  weekdayTemplates: TemplateState['sessionFormat']['weekdayTemplates'];
}): JSX.Element {
  const { t } = useTranslation('sis');
  const k = 'templateEditor.modules.sessions';
  const labels = useModuleDayLabels(module);
  const [preview, setPreview] = useState(false);

  const patchSession = (arrayIdx: number, patch: (s: SessionTpl) => SessionTpl) =>
    onChange({
      ...module,
      sessions: module.sessions.map((it, j) => (j === arrayIdx ? patch(it) : it)),
    });

  // Group sessions by dayIndex preserving authoring order; remember array index.
  const sessionsByDay = new Map<number, { session: SessionTpl; arrayIdx: number }[]>();
  module.sessions.forEach((s, arrayIdx) => {
    const arr = sessionsByDay.get(s.dayIndex) ?? [];
    arr.push({ session: s, arrayIdx });
    sessionsByDay.set(s.dayIndex, arr);
  });

  // Build interleaved timeline per day (sessions + breaks + computed clock).
  const timelineByDay = new Map<number, DayTimelineRow[]>();
  // S6: qué se recorta o no cabe en cada día (null = todo cabe entero).
  const fitByDay = new Map<number, DayFitSummary | null>();
  module.dayOffsets.forEach((dayOff, dayIdx) => {
    const wkTpl = weekdayTemplates.find((w) => w.weekday === dayOff.weekday);
    const daySessions = sessionsByDay.get(dayIdx) ?? [];
    // No jornada template for this weekday yet → still surface the sessions as
    // block-less rows so "+ Tema/Sesión" visibly work. Clock/blocks appear once
    // the FORMATO JORNADAS defines this weekday. Without this the day was skipped
    // entirely and added topics vanished from view (looked like a dead button).
    if (!wkTpl) {
      timelineByDay.set(
        dayIdx,
        daySessions.map(({ session, arrayIdx }, n) => ({
          kind: 'session' as const,
          session,
          sessionArrayIndex: arrayIdx,
          dayPosition: n,
          dayCount: daySessions.length,
          blockNumber: 0,
          startClock: '—',
          endClock: '—',
          defaultDuration: 0,
          overflow: true,
        })),
      );
      return;
    }
    // S6: la única regla de reparto (la misma del generador y del PDF).
    const layout = layoutDay(
      wkTpl.segments,
      daySessions.map((d) => ({ ...d, durationMinutes: d.session.durationMinutes })),
    );
    const clock = (min: number) => addMinutesToClock(wkTpl.startTime, min);
    const rows: DayTimelineRow[] = layout.timeline.map((e) =>
      e.kind === 'break'
        ? {
            kind: 'break' as const,
            label: e.label
              ? t(`${k}.breakNamed`, { label: e.label })
              : t(`${k}.break`, { n: e.breakNumber }),
            startClock: clock(e.startMinute),
            endClock: clock(e.endMinute),
            durationMinutes: e.endMinute - e.startMinute,
          }
        : {
            kind: 'session' as const,
            session: e.session.session,
            sessionArrayIndex: e.session.arrayIdx,
            dayPosition: e.index,
            dayCount: daySessions.length,
            blockNumber: e.blockNumber,
            startClock: clock(e.startMinute),
            endClock: clock(e.endMinute),
            defaultDuration: e.availableMinutes,
            overflow: false,
            ...(e.truncated && e.requestedMinutes !== null
              ? {
                  truncated: {
                    actual: e.actualMinutes,
                    requested: e.requestedMinutes,
                    belowMinimum: e.belowMinimum,
                  },
                }
              : {}),
          },
    );
    // Las que no encuentran bloque: «Sobra — sin bloque» (no se crean).
    for (const u of layout.unplaced) {
      rows.push({
        kind: 'session',
        session: u.session.session,
        sessionArrayIndex: u.session.arrayIdx,
        dayPosition: u.index,
        dayCount: daySessions.length,
        blockNumber: 0,
        startClock: '—',
        endClock: '—',
        defaultDuration: 0,
        overflow: true,
      });
    }
    fitByDay.set(dayIdx, dayFitSummary(layout));
    timelineByDay.set(dayIdx, rows);
  });

  /** Swap session with its neighbour of the same day. */
  const moveSession = (arrayIdx: number, dir: -1 | 1) => {
    const target = module.sessions[arrayIdx];
    if (!target) return;
    const sameDay = module.sessions
      .map((s, i) => ({ s, i }))
      .filter((x) => x.s.dayIndex === target.dayIndex);
    const pos = sameDay.findIndex((x) => x.i === arrayIdx);
    const neighbour = sameDay[pos + dir];
    if (!neighbour) return;
    const next = [...module.sessions];
    [next[arrayIdx], next[neighbour.i]] = [next[neighbour.i]!, next[arrayIdx]!];
    onChange({ ...module, sessions: next });
  };

  const noTopic = <em style={{ color: '#6b6b6b' }}>{t(`${k}.noTopic`)}</em>;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="caption" fontWeight={700}>
          {t(`${k}.title`)}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant={preview ? 'contained' : 'outlined'}
            onClick={() => setPreview((v) => !v)}
          >
            {preview ? t(`${k}.edit`) : t(`${k}.preview`)}
          </Button>
          <Button
            size="small"
            startIcon={<AddRounded />}
            onClick={() =>
              onChange({
                ...module,
                sessions: [...module.sessions, { id: uid(), dayIndex: 0, topic: '' }],
              })
            }
          >
            {t(`${k}.add`)}
          </Button>
        </Stack>
      </Stack>

      {weekdayTemplates.length === 0 && (
        <Typography variant="caption" color="warning.main" display="block" mb={1}>
          {t(`${k}.needFormat`)}
        </Typography>
      )}
      {module.dayOffsets.length === 0 && (
        <Typography variant="caption" color="warning.main" display="block" mb={1}>
          {t(`${k}.needDays`)}
        </Typography>
      )}

      {(() => {
        const noDay = module.sessions
          .map((s, i) => ({ s, i }))
          .filter((x) => hasNoDay(x.s.dayIndex, module.dayOffsets.length));
        if (noDay.length === 0) return null;
        return (
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 1.5,
              borderColor: 'warning.main',
              bgcolor: 'rgba(245,158,11,0.06)',
              mb: 2,
            }}
          >
            <Typography variant="body2" fontWeight={700} color="warning.dark" mb={1}>
              {t('templateEditor.modules.noDay.title', { count: noDay.length })}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
              {t('templateEditor.modules.noDay.body')}
            </Typography>
            <Stack spacing={1}>
              {noDay.map(({ s, i }) => (
                <Stack key={s.id} direction="row" spacing={1.5} alignItems="center">
                  <FormControl size="small" sx={{ width: 200 }}>
                    <InputLabel>{t('templateEditor.modules.noDay.assign')}</InputLabel>
                    <Select
                      label={t('templateEditor.modules.noDay.assign')}
                      value=""
                      onChange={(e) =>
                        patchSession(i, (it) => ({ ...it, dayIndex: Number(e.target.value) }))
                      }
                    >
                      {module.dayOffsets.map((_, idx) => (
                        <MenuItem key={idx} value={idx}>
                          {labels.withWeek(idx)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {s.topic || noTopic}
                  </Typography>
                  <IconButton
                    size="small"
                    color="error"
                    aria-label={t('templateEditor.modules.noDay.delete')}
                    title={t('templateEditor.modules.noDay.delete')}
                    onClick={() =>
                      onChange({
                        ...module,
                        sessions: module.sessions.filter((_, j) => j !== i),
                      })
                    }
                  >
                    <DeleteOutlineRounded fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          </Paper>
        );
      })()}

      <Stack spacing={2}>
        {module.dayOffsets.map((_, dayIdx) => {
          const rows = timelineByDay.get(dayIdx) ?? [];
          const wkTpl = weekdayTemplates.find(
            (w) => w.weekday === module.dayOffsets[dayIdx]!.weekday,
          );
          return (
            <Paper key={dayIdx} variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2" fontWeight={700}>
                  {labels.full(dayIdx)}
                  {wkTpl && (
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      {wkTpl.startTime} – {wkTpl.endTime}
                    </Typography>
                  )}
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddRounded />}
                  onClick={() =>
                    onChange({
                      ...module,
                      sessions: [...module.sessions, { id: uid(), dayIndex: dayIdx, topic: '' }],
                    })
                  }
                >
                  {t(`${k}.addHere`)}
                </Button>
              </Stack>
              {(() => {
                // S6: el aviso del día, si algo se recorta o no cabe.
                const fit = fitByDay.get(dayIdx);
                return fit && wkTpl ? (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    {fitSentence(t, fit, wkTpl.weekday)}
                  </Alert>
                ) : null;
              })()}
              {rows.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  {t(`${k}.emptyDay`)}
                </Typography>
              ) : preview ? (
                <Stack spacing={0.5}>
                  {rows.map((r, i) =>
                    r.kind === 'break' ? (
                      <Stack
                        key={`b-${i}`}
                        direction="row"
                        spacing={2}
                        sx={{ px: 1.5, py: 0.5, bgcolor: 'rgba(245,158,11,0.06)', borderRadius: 1 }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ fontFamily: 'monospace', minWidth: 120 }}
                        >
                          {r.startClock} → {r.endClock}
                        </Typography>
                        <Typography variant="caption" color="warning.dark">
                          {t(`${k}.breakPreview`, { label: r.label, minutes: r.durationMinutes })}
                        </Typography>
                      </Stack>
                    ) : (
                      <Stack
                        key={r.session.id}
                        direction="row"
                        spacing={2}
                        sx={{
                          px: 1.5,
                          py: 0.75,
                          bgcolor: r.overflow ? 'rgba(245,158,11,0.1)' : 'rgba(99,102,241,0.06)',
                          borderRadius: 1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ fontFamily: 'monospace', minWidth: 120 }}
                        >
                          {r.startClock} → {r.endClock}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ minWidth: 70 }}
                          color={r.overflow ? 'warning.dark' : 'primary'}
                        >
                          {r.overflow ? t(`${k}.overflow`) : t(`${k}.block`, { n: r.blockNumber })}
                        </Typography>
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {r.session.topic || noTopic}
                        </Typography>
                        {r.truncated && <TruncatedChip truncated={r.truncated} />}
                        {r.session.scriptureRefs && r.session.scriptureRefs.length > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            {formatScriptureRefs(r.session.scriptureRefs)}
                          </Typography>
                        )}
                      </Stack>
                    ),
                  )}
                </Stack>
              ) : (
                <Stack spacing={1}>
                  {rows.map((r, i) =>
                    r.kind === 'break' ? (
                      <Stack
                        key={`b-${i}`}
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        sx={{
                          px: 1.5,
                          py: 0.75,
                          bgcolor: 'rgba(245,158,11,0.06)',
                          borderRadius: 1,
                          border: '1px dashed rgba(245,158,11,0.4)',
                        }}
                      >
                        <Chip
                          size="small"
                          color="warning"
                          variant="outlined"
                          label={r.label}
                          sx={{ minWidth: 100 }}
                        />
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1 }}>
                          {r.startClock} → {r.endClock}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t(`${k}.breakNote`, { minutes: r.durationMinutes })}
                        </Typography>
                      </Stack>
                    ) : (
                      <Stack
                        key={r.session.id}
                        spacing={1}
                        sx={{
                          px: 1,
                          py: 0.75,
                          borderRadius: 1,
                          bgcolor: r.overflow ? 'rgba(245,158,11,0.08)' : 'transparent',
                          border: r.overflow ? '1px solid rgba(245,158,11,0.4)' : 'none',
                        }}
                      >
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          {/* S6: la etiqueta «recortada» va debajo del bloque, en la
                              misma columna, para que las filas sigan alineadas. */}
                          <Stack spacing={0.5} sx={{ width: 200, flexShrink: 0 }}>
                            <Chip
                              size="small"
                              color={r.overflow ? 'warning' : 'primary'}
                              variant="outlined"
                              label={
                                r.overflow
                                  ? t(`${k}.overflowChip`)
                                  : t(`${k}.blockClock`, {
                                      n: r.blockNumber,
                                      start: r.startClock,
                                      end: r.endClock,
                                    })
                              }
                              sx={{ width: 200 }}
                            />
                            {r.truncated && <TruncatedChip truncated={r.truncated} />}
                          </Stack>
                          <FormControl size="small" sx={{ width: 160 }}>
                            <InputLabel>{t(`${k}.day`)}</InputLabel>
                            <Select
                              label={t(`${k}.day`)}
                              value={r.session.dayIndex}
                              onChange={(e) =>
                                patchSession(r.sessionArrayIndex, (it) => ({
                                  ...it,
                                  dayIndex: Number(e.target.value),
                                }))
                              }
                            >
                              {module.dayOffsets.map((_, idx) => (
                                <MenuItem key={idx} value={idx}>
                                  {labels.short(idx)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <TextField
                            size="small"
                            label={t(`${k}.topic`)}
                            value={r.session.topic}
                            onChange={(e) =>
                              patchSession(r.sessionArrayIndex, (it) => ({
                                ...it,
                                topic: e.target.value,
                              }))
                            }
                            sx={{ flex: 1 }}
                          />
                          <IconButton
                            size="small"
                            disabled={r.dayPosition === 0}
                            onClick={() => moveSession(r.sessionArrayIndex, -1)}
                            aria-label={t(`${k}.moveUp`)}
                            title={t(`${k}.moveUp`)}
                          >
                            <ChevronLeftRounded
                              sx={{ transform: 'rotate(90deg)' }}
                              fontSize="small"
                            />
                          </IconButton>
                          <IconButton
                            size="small"
                            disabled={r.dayPosition === r.dayCount - 1}
                            onClick={() => moveSession(r.sessionArrayIndex, 1)}
                            aria-label={t(`${k}.moveDown`)}
                            title={t(`${k}.moveDown`)}
                          >
                            <ChevronRightRounded
                              sx={{ transform: 'rotate(90deg)' }}
                              fontSize="small"
                            />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="warning"
                            aria-label={t(`${k}.unassign`)}
                            title={t(`${k}.unassign`)}
                            onClick={() =>
                              patchSession(r.sessionArrayIndex, (it) => ({ ...it, dayIndex: -1 }))
                            }
                          >
                            <DeleteOutlineRounded fontSize="small" />
                          </IconButton>
                        </Stack>
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="flex-start"
                          sx={{ pl: { md: '212px' } }}
                        >
                          <SessionKeyTextsField
                            value={r.session.scriptureRefs}
                            onChange={(refs) =>
                              patchSession(r.sessionArrayIndex, (it) => {
                                const { scriptureRefs: _drop, ...rest } = it;
                                return refs ? { ...rest, scriptureRefs: refs } : rest;
                              })
                            }
                          />
                          <TextField
                            size="small"
                            type="number"
                            label={t(`${k}.minutes`)}
                            placeholder={String(r.defaultDuration)}
                            title={t(`${k}.minutesHelp`)}
                            value={r.session.durationMinutes ?? ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              patchSession(r.sessionArrayIndex, (it) => {
                                if (v === '') {
                                  const { durationMinutes: _drop, ...rest } = it;
                                  return rest;
                                }
                                return { ...it, durationMinutes: Number(v) };
                              });
                            }}
                            sx={{ width: 150 }}
                          />
                        </Stack>
                      </Stack>
                    ),
                  )}
                </Stack>
              )}
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
}

function AssignmentsSection({
  state,
  setState,
  availableRubrics,
}: SectionProps & { availableRubrics: { id: string; title: string }[] }): JSX.Element {
  const { t } = useTranslation('sis');
  const [noteOpen, setNoteOpen] = useState(false);
  const update = (a: AssignTpl[]) => setState({ ...state, assignments: a });
  const refsSet = state.assignments.filter((a) => Boolean(a.rubricRef)).length;
  const showRubricHint =
    availableRubrics.length > 0 && state.assignments.length > 0 && refsSet === 0;
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack spacing={2.5}>
        {showRubricHint && (
          <Alert severity="warning" variant="outlined">
            El programa tiene {availableRubrics.length} rúbrica
            {availableRubrics.length === 1 ? '' : 's'} configurada
            {availableRubrics.length === 1 ? '' : 's'} pero ningún assignment las referencia
            todavía. Las rúbricas <strong>no aparecerán en el syllabus PDF</strong> hasta que elijas
            una en el dropdown "Rúbrica" de cada assignment relevante.
          </Alert>
        )}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2" fontWeight={700}>
            {t('templateEditor.assignments.instances')}
          </Typography>
          <Button
            size="small"
            startIcon={<AddRounded />}
            onClick={() =>
              update([
                ...state.assignments,
                {
                  id: uid(),
                  type: 'reading-review',
                  name: 'Nueva entrega',
                  description: '',
                  weekOffset: 0,
                  weight: 0,
                  gradeComponentId: state.grading.components[0]?.id ?? '',
                  pointsPossible: 100,
                  submissionTypes: ['file'],
                },
              ])
            }
          >
            Agregar
          </Button>
        </Stack>
        {/* Collapsible optional section description — starts collapsed to cut scroll. */}
        <CollapsibleNote
          title={t('templateEditor.assignments.noteTitle')}
          caption={t('templateEditor.assignments.noteCaption')}
          open={noteOpen}
          onToggle={() => setNoteOpen((o) => !o)}
          hasContent={Boolean(state.assignmentsNote)}
        >
          <PageEditor
            value={state.assignmentsNote ?? null}
            onChange={(json) => setState({ ...state, assignmentsNote: json })}
            placeholder={t('templateEditor.assignments.notePlaceholder')}
          />
        </CollapsibleNote>
        {state.assignments.map((a, i) => (
          <Paper key={a.id} variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <FormControl size="small" sx={{ width: 180 }}>
                  <InputLabel>{t('templateEditor.assignments.typeLabel')}</InputLabel>
                  <Select
                    label={t('templateEditor.assignments.typeLabel')}
                    value={a.type}
                    onChange={(e) => {
                      const nextType = e.target.value as AssignTpl['type'];
                      update(
                        state.assignments.map((it, j) => {
                          if (j !== i) return it;
                          // Changing the type must never leave an upload-based
                          // assignment without a channel: the portal submit
                          // dialog renders its inputs off `submissionTypes`, so
                          // an empty array means the student sees nothing.
                          const submissionTypes = !requiresSubmissionChannel(nextType)
                            ? defaultSubmissionTypes(nextType)
                            : it.submissionTypes?.length
                              ? it.submissionTypes
                              : defaultSubmissionTypes(nextType);
                          return { ...it, type: nextType, submissionTypes };
                        }),
                      );
                    }}
                  >
                    <MenuItem value="reading-review">
                      {t('templateEditor.assignments.type.readingReview')}
                    </MenuItem>
                    <MenuItem value="essay">{t('templateEditor.assignments.type.essay')}</MenuItem>
                    <MenuItem value="quiz">{t('templateEditor.assignments.type.quiz')}</MenuItem>
                    <MenuItem value="project">
                      {t('templateEditor.assignments.type.project')}
                    </MenuItem>
                    <MenuItem value="attendance">
                      {t('templateEditor.assignments.type.attendance')}
                    </MenuItem>
                    <MenuItem value="other">{t('templateEditor.assignments.type.other')}</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Nombre"
                  value={a.name}
                  onChange={(e) =>
                    update(
                      state.assignments.map((it, j) =>
                        j === i ? { ...it, name: e.target.value } : it,
                      ),
                    )
                  }
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  label="Sem #"
                  type="number"
                  // weekOffset is stored 0-based (materialization adds
                  // weekOffset*7 days to term start). The field shows it
                  // 1-based so "Sem 1" == first week, matching the PDF +
                  // portal which render `weekOffset + 1`.
                  value={a.weekOffset + 1}
                  onChange={(e) =>
                    update(
                      state.assignments.map((it, j) =>
                        j === i
                          ? { ...it, weekOffset: Math.max(0, Number(e.target.value) - 1) }
                          : it,
                      ),
                    )
                  }
                  sx={{ width: 90 }}
                />
                <FormControl size="small" sx={{ width: 200 }}>
                  <InputLabel>Módulo</InputLabel>
                  <Select
                    label="Módulo"
                    value={
                      a.templateModuleId && state.modules.some((m) => m.id === a.templateModuleId)
                        ? a.templateModuleId
                        : ''
                    }
                    onChange={(e) => {
                      const v = e.target.value;
                      update(
                        state.assignments.map((it, j) => {
                          if (j !== i) return it;
                          // Omit the key when cleared (exactOptionalPropertyTypes).
                          const { templateModuleId: _drop, ...rest } = it;
                          return v ? { ...rest, templateModuleId: v } : rest;
                        }),
                      );
                    }}
                  >
                    <MenuItem value="">
                      <em>Sin módulo</em>
                    </MenuItem>
                    {state.modules.map((m) => (
                      <MenuItem key={m.id} value={m.id}>
                        {m.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Peso %"
                  type="number"
                  value={a.weight}
                  onChange={(e) =>
                    update(
                      state.assignments.map((it, j) =>
                        j === i ? { ...it, weight: Number(e.target.value) } : it,
                      ),
                    )
                  }
                  sx={{ width: 90 }}
                />
                <FormControl size="small" sx={{ width: 200 }}>
                  <InputLabel>Componente</InputLabel>
                  <Select
                    label="Componente"
                    value={a.gradeComponentId}
                    onChange={(e) =>
                      update(
                        state.assignments.map((it, j) =>
                          j === i ? { ...it, gradeComponentId: e.target.value as string } : it,
                        ),
                      )
                    }
                  >
                    {state.grading.components.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Pts"
                  type="number"
                  value={a.pointsPossible}
                  onChange={(e) =>
                    update(
                      state.assignments.map((it, j) =>
                        j === i ? { ...it, pointsPossible: Number(e.target.value) } : it,
                      ),
                    )
                  }
                  sx={{ width: 90 }}
                />
                <IconButton
                  color="error"
                  onClick={() => update(state.assignments.filter((_, j) => j !== i))}
                >
                  <DeleteOutlineRounded />
                </IconButton>
              </Stack>
              <TextField
                size="small"
                label="Descripción / instrucciones"
                multiline
                rows={2}
                value={a.description}
                onChange={(e) =>
                  update(
                    state.assignments.map((it, j) =>
                      j === i ? { ...it, description: e.target.value } : it,
                    ),
                  )
                }
              />
              <FormControl size="small" sx={{ maxWidth: 360 }}>
                <InputLabel id={`rubric-${a.id}-label`}>Rúbrica</InputLabel>
                <Select
                  labelId={`rubric-${a.id}-label`}
                  label="Rúbrica"
                  value={a.rubricRef ?? ''}
                  onChange={(e) => {
                    const v = e.target.value as string;
                    update(
                      state.assignments.map((it, j) => {
                        if (j !== i) return it;
                        if (!v) {
                          const { rubricRef: _drop, ...rest } = it;
                          void _drop;
                          return rest;
                        }
                        return { ...it, rubricRef: v };
                      }),
                    );
                  }}
                >
                  <MenuItem value="">
                    <em>Sin rúbrica</em>
                  </MenuItem>
                  {availableRubrics.map((r) => (
                    <MenuItem key={r.id} value={r.id}>
                      {r.title || r.id}
                    </MenuItem>
                  ))}
                </Select>
                {availableRubrics.length === 0 && (
                  <Typography variant="caption" color="warning.main" mt={0.5}>
                    {state.programId
                      ? 'El programa vinculado no tiene rúbricas. Configurá rúbricas en el programa antes de referenciarlas aquí.'
                      : 'Vinculá esta plantilla a un programa en la pestaña Identidad para elegir una rúbrica de su catálogo.'}
                  </Typography>
                )}
              </FormControl>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Paper>
  );
}

function PoliciesSection({ state, setState, inherited }: SectionProps): JSX.Element {
  const { t } = useTranslation('sis');
  const lp = state.latePolicy ?? {
    firstWindowHours: 6,
    firstWindowPenalty: 5,
    secondWindowHours: 24,
    secondWindowPenalty: 10,
    dailyPenaltyAfter: 1,
  };
  const defaultPoliciesNote =
    'Los estudiantes deben entregar las tareas en las fechas estipuladas. Las entregas tardías reciben las penalizaciones detalladas a continuación.';
  const inhPolicies = inherited?.policies ?? [];
  const inhLate = inherited?.latePolicy;
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      {inherited && (
        <Box mb={2}>
          <Stack spacing={1.5}>
            <InheritedBlock
              label={t('templateEditor.inherited.policies')}
              hasContent={inhPolicies.length > 0}
              emptyHint={t('templateEditor.inherited.emptyProgram')}
            >
              <Stack component="ul" sx={{ m: 0, pl: 2.5 }} spacing={0.25}>
                {inhPolicies.map((p) => (
                  <Typography key={p.id} component="li" variant="body2" color="text.secondary">
                    {p.title}
                  </Typography>
                ))}
              </Stack>
            </InheritedBlock>
            {inhLate && (
              <InheritedBlock
                label={t('templateEditor.inherited.latePolicy')}
                hasContent
                emptyHint=""
              >
                <Typography variant="body2" color="text.secondary">
                  {inhLate.firstWindowPenalty}% (&lt;{inhLate.firstWindowHours}h) ·{' '}
                  {inhLate.secondWindowPenalty}% (&lt;{inhLate.secondWindowHours}h) ·{' '}
                  {inhLate.dailyPenaltyAfter}%/día después
                </Typography>
              </InheritedBlock>
            )}
          </Stack>
        </Box>
      )}
      <Stack spacing={2}>
        <Typography variant="subtitle2" fontWeight={700}>
          Política de atraso
        </Typography>
        <TextField
          size="small"
          label="Descripción de las políticas (opcional)"
          placeholder={defaultPoliciesNote}
          value={state.policiesNote ?? defaultPoliciesNote}
          onChange={(e) => setState({ ...state, policiesNote: e.target.value })}
          multiline
          minRows={2}
          fullWidth
        />
        <Stack direction="row" spacing={1}>
          <TextField
            label="1ª ventana (h)"
            size="small"
            type="number"
            value={lp.firstWindowHours}
            onChange={(e) =>
              setState({
                ...state,
                latePolicy: { ...lp, firstWindowHours: Number(e.target.value) },
              })
            }
          />
          <TextField
            label="% penalidad"
            size="small"
            type="number"
            value={lp.firstWindowPenalty}
            onChange={(e) =>
              setState({
                ...state,
                latePolicy: { ...lp, firstWindowPenalty: Number(e.target.value) },
              })
            }
          />
          <TextField
            label="2ª ventana (h)"
            size="small"
            type="number"
            value={lp.secondWindowHours}
            onChange={(e) =>
              setState({
                ...state,
                latePolicy: { ...lp, secondWindowHours: Number(e.target.value) },
              })
            }
          />
          <TextField
            label="% penalidad"
            size="small"
            type="number"
            value={lp.secondWindowPenalty}
            onChange={(e) =>
              setState({
                ...state,
                latePolicy: { ...lp, secondWindowPenalty: Number(e.target.value) },
              })
            }
          />
          <TextField
            label="% por día"
            size="small"
            type="number"
            value={lp.dailyPenaltyAfter}
            onChange={(e) =>
              setState({
                ...state,
                latePolicy: { ...lp, dailyPenaltyAfter: Number(e.target.value) },
              })
            }
          />
        </Stack>

        <Divider />

        <Typography variant="subtitle2" fontWeight={700}>
          Versículo eje (opcional)
        </Typography>
        <InheritedFromCourseBanner courseId={state.courseId} fieldLabel="Versículo eje" />
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            label="Referencia"
            value={state.anchorVerse?.reference ?? ''}
            disabled={Boolean(state.courseId)}
            onChange={(e) =>
              setState({
                ...state,
                anchorVerse: {
                  reference: e.target.value,
                  text: state.anchorVerse?.text ?? '',
                  translation: state.anchorVerse?.translation ?? 'RV1960',
                },
              })
            }
            sx={{ width: 200 }}
          />
          <TextField
            size="small"
            label="Texto"
            value={state.anchorVerse?.text ?? ''}
            disabled={Boolean(state.courseId)}
            onChange={(e) =>
              setState({
                ...state,
                anchorVerse: {
                  reference: state.anchorVerse?.reference ?? '',
                  text: e.target.value,
                  translation: state.anchorVerse?.translation ?? 'RV1960',
                },
              })
            }
            sx={{ flex: 1 }}
          />
          <TextField
            size="small"
            label="Traducción"
            value={state.anchorVerse?.translation ?? 'RV1960'}
            disabled={Boolean(state.courseId)}
            onChange={(e) =>
              setState({
                ...state,
                anchorVerse: {
                  reference: state.anchorVerse?.reference ?? '',
                  text: state.anchorVerse?.text ?? '',
                  translation: e.target.value,
                },
              })
            }
            sx={{ width: 120 }}
          />
        </Stack>
      </Stack>
    </Paper>
  );
}
